import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Shield, 
  AlertTriangle, 
  Users, 
  Calendar, 
  Flag,
  CheckCircle,
  XCircle,
  Trash2,
  Ban,
  Eye,
  BookOpen,
  Plus,
  Edit,
  EyeOff,
  Briefcase,
  Building,
  Clock,
  Home,
  ShoppingBag,
  Tag,
  Settings,
  RefreshCw,
  RotateCcw,
  Image,
  Upload,
  Lock,
  Unlock,
  UserPlus,
  UserMinus,
  Search,
  X,
  Check,
  Mail,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MapPin,
  Globe,
  UserX,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Bell,
  BarChart3
} from 'lucide-react';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [moderationQueue, setModerationQueue] = useState([]);
  const [articles, setArticles] = useState([]);
  const [pendingJobs, setPendingJobs] = useState([]);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [pendingProperties, setPendingProperties] = useState([]);
  const [pendingMarketplace, setPendingMarketplace] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [allProperties, setAllProperties] = useState([]);
  const [allMarketplaceListings, setAllMarketplaceListings] = useState([]);
  const [jobsFilter, setJobsFilter] = useState('pending');
  const [businessesFilter, setBusinessesFilter] = useState('pending');
  const [propertiesFilter, setPropertiesFilter] = useState('pending');
  const [marketplaceFilter, setMarketplaceFilter] = useState('pending');
  const [moderationStats, setModerationStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [deletedEventsList, setDeletedEventsList] = useState([]);
  const [restoringEventId, setRestoringEventId] = useState(null);
  const [permanentDeletingId, setPermanentDeletingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportFilter, setReportFilter] = useState('pending');
  const [actionLoading, setActionLoading] = useState(null);
  const [campaign2yearPreview, setCampaign2yearPreview] = useState(null);
  const [privateGroupCreators, setPrivateGroupCreators] = useState([]);
  const [permissionRequests, setPermissionRequests] = useState([]);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  
  // Members tab state
  const [membersSearch, setMembersSearch] = useState('');
  const [membersSort, setMembersSort] = useState({ field: 'name', dir: 'asc' });
  const [membersFilter, setMembersFilter] = useState('all');
  
  // Group members expansion state
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupMembers, setGroupMembers] = useState({});
  const [groupMembersSort, setGroupMembersSort] = useState({});
  const [groupMembersLoading, setGroupMembersLoading] = useState({});
  
  // Article form state
  const [showArticleDialog, setShowArticleDialog] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [articleForm, setArticleForm] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    category: 'lifestyle',
    featured_image: '',
    tags: ''
  });
  const [savingArticle, setSavingArticle] = useState(false);

  // Event image edit state
  const [showEventImageDialog, setShowEventImageDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventImageUrl, setEventImageUrl] = useState('');
  const [eventImageFile, setEventImageFile] = useState(null);
  const [savingEventImage, setSavingEventImage] = useState(false);

  const ARTICLE_CATEGORIES = [
    { id: 'relocation', name: 'Relocation', icon: '🏠' },
    { id: 'lifestyle', name: 'Lifestyle', icon: '🌟' },
    { id: 'finance', name: 'Finance', icon: '💰' },
    { id: 'culture', name: 'Culture', icon: '🎭' },
    { id: 'work', name: 'Work & Career', icon: '💼' },
    { id: 'family', name: 'Family', icon: '👨‍👩‍👧' }
  ];

  // Computed values for current and past events
  const today = new Date().toISOString().split('T')[0];
  const currentEvents = allEvents.filter(event => event.event_date >= today);
  const pastEvents = allEvents.filter(event => event.event_date < today);

  const [activeTab, setActiveTab] = useState('jobs');
  const [loadedTabs, setLoadedTabs] = useState({});
  const [engagementStats, setEngagementStats] = useState(null);

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Only refetch reports when filter changes
  useEffect(() => {
    if (loadedTabs.reports) {
      fetchTabData('reports');
    }
  }, [reportFilter]);

  // Load data for the active tab when it changes
  useEffect(() => {
    if (!loadedTabs[activeTab]) {
      fetchTabData(activeTab);
    }
    if (activeTab === 'tools' && !engagementStats) {
      axios.get(`${API}/admin/engagement-stats`).then(r => setEngagementStats(r.data)).catch(() => {});
    }
  }, [activeTab]);

  const fetchInitialData = async () => {
    try {
      // Only load stats and moderation stats on initial render — fast!
      const [statsRes, modStatsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`).catch(e => ({ data: {} })),
        axios.get(`${API}/admin/moderation-stats`).catch(e => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setModerationStats(modStatsRes.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
    // Also load the default tab data
    fetchTabData('jobs');
  };

  const fetchTabData = async (tab) => {
    try {
      switch (tab) {
        case 'events':
        case 'past-events': {
          if (!loadedTabs.events) {
            const [eventsRes, groupsRes] = await Promise.all([
              axios.get(`${API}/events?include_past=true`).catch(e => ({ data: [] })),
              axios.get(`${API}/admin/all-groups`).catch(e => ({ data: [] })),
            ]);
            setAllEvents(eventsRes.data.events || eventsRes.data || []);
            setAllGroups(groupsRes.data || []);
            setLoadedTabs(prev => ({ ...prev, events: true, 'past-events': true }));
          }
          break;
        }
        case 'trash': {
          if (!loadedTabs.trash) {
            try {
              const res = await axios.get(`${API}/admin/events/deleted`);
              setDeletedEventsList(res.data.events || []);
              setLoadedTabs(prev => ({ ...prev, trash: true }));
            } catch (err) {
              toast.error('Failed to load deleted events');
            }
          }
          break;
        }
        case 'jobs': {
          const [pendingRes, allRes] = await Promise.all([
            axios.get(`${API}/admin/pending-jobs`).catch(e => ({ data: { jobs: [] } })),
            axios.get(`${API}/jobs`).catch(e => ({ data: { jobs: [] } })),
          ]);
          setPendingJobs(pendingRes.data.jobs || []);
          setAllJobs(allRes.data.jobs || []);
          setLoadedTabs(prev => ({ ...prev, jobs: true }));
          break;
        }
        case 'businesses': {
          const [pendingRes, allRes] = await Promise.all([
            axios.get(`${API}/admin/pending-businesses`).catch(e => ({ data: { businesses: [] } })),
            axios.get(`${API}/businesses`).catch(e => ({ data: { businesses: [] } })),
          ]);
          setPendingBusinesses(pendingRes.data.businesses || []);
          setAllBusinesses(allRes.data.businesses || []);
          setLoadedTabs(prev => ({ ...prev, businesses: true }));
          break;
        }
        case 'properties': {
          const [pendingRes, allRes] = await Promise.all([
            axios.get(`${API}/admin/pending-properties`).catch(e => ({ data: { properties: [] } })),
            axios.get(`${API}/housing`).catch(e => ({ data: { properties: [] } })),
          ]);
          setPendingProperties(pendingRes.data.properties || []);
          setAllProperties(allRes.data.properties || []);
          setLoadedTabs(prev => ({ ...prev, properties: true }));
          break;
        }
        case 'marketplace': {
          const [pendingRes, allRes] = await Promise.all([
            axios.get(`${API}/admin/pending-marketplace`).catch(e => ({ data: { listings: [] } })),
            axios.get(`${API}/marketplace`).catch(e => ({ data: { listings: [] } })),
          ]);
          setPendingMarketplace(pendingRes.data.listings || []);
          setAllMarketplaceListings(allRes.data.listings || []);
          setLoadedTabs(prev => ({ ...prev, marketplace: true }));
          break;
        }
        case 'reports': {
          const [reportsRes, queueRes] = await Promise.all([
            axios.get(`${API}/admin/reports?report_status=${reportFilter}`).catch(e => ({ data: [] })),
            axios.get(`${API}/admin/moderation-queue`).catch(e => ({ data: [] })),
          ]);
          setReports(reportsRes.data);
          setModerationQueue(queueRes.data);
          setLoadedTabs(prev => ({ ...prev, reports: true }));
          break;
        }
        case 'articles': {
          const res = await axios.get(`${API}/admin/articles`).catch(e => ({ data: [] }));
          setArticles(res.data);
          setLoadedTabs(prev => ({ ...prev, articles: true }));
          break;
        }
        case 'users': {
          const res = await axios.get(`${API}/admin/users`).catch(e => ({ data: [] }));
          setUsers(res.data || []);
          setLoadedTabs(prev => ({ ...prev, users: true }));
          break;
        }
        case 'permissions': {
          const [creatorsRes, requestsRes] = await Promise.all([
            axios.get(`${API}/admin/private-group-creators`).catch(e => ({ data: { users: [] } })),
            axios.get(`${API}/admin/private-group-requests`).catch(e => ({ data: [] })),
          ]);
          setPrivateGroupCreators(creatorsRes.data.users || []);
          setPermissionRequests(requestsRes.data || []);
          setLoadedTabs(prev => ({ ...prev, permissions: true }));
          break;
        }
        case 'groups': {
          const res = await axios.get(`${API}/admin/all-groups`).catch(e => ({ data: [] }));
          setAllGroups(res.data || []);
          setLoadedTabs(prev => ({ ...prev, groups: true }));
          break;
        }
        default:
          setLoadedTabs(prev => ({ ...prev, [tab]: true }));
          break;
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/');
      }
    }
  };

  // Refresh current tab data (used after admin actions)
  const refreshCurrentTab = () => {
    setLoadedTabs(prev => ({ ...prev, [activeTab]: false }));
    fetchTabData(activeTab);
    // Also refresh stats
    axios.get(`${API}/admin/stats`).then(r => setStats(r.data)).catch(() => {});
    axios.get(`${API}/admin/moderation-stats`).then(r => setModerationStats(r.data)).catch(() => {});
  };

  const toggleGroupExpanded = async (slug) => {
    const isExpanded = expandedGroups[slug];
    setExpandedGroups(prev => ({ ...prev, [slug]: !isExpanded }));
    
    // Load members if not yet loaded
    if (!isExpanded && !groupMembers[slug]) {
      setGroupMembersLoading(prev => ({ ...prev, [slug]: true }));
      try {
        const res = await axios.get(`${API}/admin/groups/${slug}/members`);
        setGroupMembers(prev => ({ ...prev, [slug]: res.data.members }));
        if (!groupMembersSort[slug]) {
          setGroupMembersSort(prev => ({ ...prev, [slug]: { field: 'name', dir: 'asc' } }));
        }
      } catch {
        toast.error('Failed to load group members');
      } finally {
        setGroupMembersLoading(prev => ({ ...prev, [slug]: false }));
      }
    }
  };

  const getLastNameHelper = (name) => {
    if (!name) return '';
    const parts = name.trim().split(/\s+/);
    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
  };

  const sortGroupMembers = (members, sort) => {
    if (!members || !sort) return members || [];
    return [...members].sort((a, b) => {
      let valA, valB;
      switch (sort.field) {
        case 'last_name':
          valA = getLastNameHelper(a.name);
          valB = getLastNameHelper(b.name);
          break;
        case 'city':
          valA = (a.residence_city || '').toLowerCase();
          valB = (b.residence_city || '').toLowerCase();
          break;
        case 'country':
          valA = (a.residence_country || '').toLowerCase();
          valB = (b.residence_country || '').toLowerCase();
          break;
        default:
          valA = (a.name || '').toLowerCase();
          valB = (b.name || '').toLowerCase();
      }
      if (valA < valB) return sort.dir === 'asc' ? -1 : 1;
      if (valA > valB) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const toggleGroupMemberSort = (slug, field) => {
    setGroupMembersSort(prev => {
      const current = prev[slug] || { field: 'name', dir: 'asc' };
      return {
        ...prev,
        [slug]: current.field === field
          ? { field, dir: current.dir === 'asc' ? 'desc' : 'asc' }
          : { field, dir: 'asc' }
      };
    });
  };

  const handleReportAction = async (reportId, action) => {
    try {
      await axios.put(`${API}/admin/reports/${reportId}?action=${action}`);
      toast.success(`Report ${action === 'dismiss' ? 'dismissed' : 'action taken'}`);
      refreshCurrentTab();
    } catch (error) {
      toast.error('Failed to process report');
    }
  };

  const handleModerationAction = async (contentId, action) => {
    try {
      await axios.put(`${API}/admin/moderation-queue/${contentId}?action=${action}`);
      toast.success(`Content ${action}d successfully`);
      refreshCurrentTab();
    } catch (error) {
      toast.error('Failed to process moderation');
    }
  };

  const handleDeleteEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Move "${eventTitle}" to trash? It can be restored within 30 days.`)) {
      return;
    }
    setActionLoading(eventId);
    try {
      await axios.delete(`${API}/admin/events/${eventId}`);
      toast.success('Event moved to trash. Auto-purges after 30 days.');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setActionLoading(null);
    }
  };
  
  const handleRestoreDeletedEvent = async (eventId, eventTitle) => {
    setRestoringEventId(eventId);
    try {
      await axios.post(`${API}/events/${eventId}/restore`);
      toast.success(`"${eventTitle}" restored`);
      // Refresh the deleted list
      try {
        const res = await axios.get(`${API}/admin/events/deleted`);
        setDeletedEventsList(res.data.events || []);
      } catch {}
      // Also invalidate the events tab so it shows the restored event
      setLoadedTabs(prev => ({ ...prev, events: false, 'past-events': false }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore event');
    } finally {
      setRestoringEventId(null);
    }
  };
  
  const handlePermanentDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`⚠️ PERMANENTLY delete "${eventTitle}"?\n\nThis cannot be undone. The event and all comments will be permanently erased from the database.`)) {
      return;
    }
    setPermanentDeletingId(eventId);
    try {
      await axios.delete(`${API}/admin/events/${eventId}/permanent`);
      toast.success('Event permanently deleted');
      setDeletedEventsList(prev => prev.filter(e => e.event_id !== eventId));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to permanently delete event');
    } finally {
      setPermanentDeletingId(null);
    }
  };

  // Event Image Edit Functions
  const openEventImageDialog = (event) => {
    setEditingEvent(event);
    setEventImageUrl(event.image_url || '');
    setEventImageFile(null);
    setShowEventImageDialog(true);
  };

  const handleEventImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Image too large. Max size is 2MB');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventImageFile(reader.result);
        setEventImageUrl(''); // Clear URL when file is selected
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEventImage = async () => {
    if (!editingEvent) return;
    
    setSavingEventImage(true);
    try {
      let imageToSave = eventImageUrl;
      
      // If file was uploaded, use the base64 data
      if (eventImageFile) {
        imageToSave = eventImageFile;
      }
      
      await axios.patch(`${API}/admin/events/${editingEvent.event_id}/image`, {
        image_url: imageToSave
      });
      
      toast.success('Event image updated successfully');
      setShowEventImageDialog(false);
      setEditingEvent(null);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event image');
    } finally {
      setSavingEventImage(false);
    }
  };

  const handleToggleInviteOnly = async (event) => {
    setActionLoading(event.event_id);
    try {
      const newStatus = !event.is_invite_only;
      await axios.patch(`${API}/admin/events/${event.event_id}/invite-only`, {
        is_invite_only: newStatus
      });
      toast.success(newStatus ? 'Event is now invite-only' : 'Event is now open to all');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setActionLoading(null);
    }
  };

  // Job & Business & Property Moderation Functions
  const handleJobModeration = async (jobId, action, reason = null) => {
    setActionLoading(jobId);
    try {
      await axios.put(`${API}/admin/jobs/${jobId}/moderate`, { action, reason });
      toast.success(`Job ${action === 'reject' ? 'rejected' : 'approved'} successfully`);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBusinessModeration = async (businessId, action, reason = null) => {
    setActionLoading(businessId);
    try {
      await axios.put(`${API}/admin/businesses/${businessId}/moderate`, { action, reason });
      toast.success(`Business ${action === 'reject' ? 'rejected' : 'approved'} successfully`);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate business');
    } finally {
      setActionLoading(null);
    }
  };

  const handlePropertyModeration = async (propertyId, action, reason = null) => {
    setActionLoading(propertyId);
    try {
      await axios.put(`${API}/admin/properties/${propertyId}/moderate`, { action, reason });
      toast.success(`Property ${action === 'reject' ? 'rejected' : 'approved'} successfully`);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarketplaceModeration = async (listingId, action, reason = null) => {
    setActionLoading(listingId);
    try {
      await axios.put(`${API}/admin/marketplace/${listingId}/moderate`, { action, reason });
      toast.success(`Listing ${action === 'reject' ? 'rejected' : 'approved'} successfully`);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to moderate listing');
    } finally {
      setActionLoading(null);
    }
  };

  // Delete handlers for content management
  const handleDeleteJob = async (jobId, jobTitle) => {
    if (!window.confirm(`Delete job "${jobTitle}"? This cannot be undone.`)) return;
    setActionLoading(jobId);
    try {
      await axios.delete(`${API}/admin/jobs/${jobId}`);
      toast.success('Job deleted successfully');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteProperty = async (propertyId, propertyTitle) => {
    if (!window.confirm(`Delete property "${propertyTitle}"? This cannot be undone.`)) return;
    setActionLoading(propertyId);
    try {
      await axios.delete(`${API}/admin/properties/${propertyId}`);
      toast.success('Property deleted successfully');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete property');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteMarketplaceListing = async (listingId, listingTitle) => {
    if (!window.confirm(`Delete listing "${listingTitle}"? This cannot be undone.`)) return;
    setActionLoading(listingId);
    try {
      await axios.delete(`${API}/admin/marketplace/${listingId}`);
      toast.success('Listing deleted successfully');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteBusiness = async (businessId, businessName) => {
    if (!window.confirm(`Delete business "${businessName}"? This cannot be undone.`)) return;
    setActionLoading(businessId);
    try {
      await axios.delete(`${API}/admin/businesses/${businessId}`);
      toast.success('Business deleted successfully');
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete business');
    } finally {
      setActionLoading(null);
    }
  };

  // Article Management Functions
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const openNewArticle = () => {
    setEditingArticle(null);
    setArticleForm({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      category: 'lifestyle',
      featured_image: '',
      tags: ''
    });
    setShowArticleDialog(true);
  };

  const openEditArticle = (article) => {
    setEditingArticle(article);
    setArticleForm({
      title: article.title,
      slug: article.slug,
      content: article.content,
      excerpt: article.excerpt || '',
      category: article.category,
      featured_image: article.featured_image || '',
      tags: article.tags?.join(', ') || ''
    });
    setShowArticleDialog(true);
  };

  const handleSaveArticle = async () => {
    if (!articleForm.title || !articleForm.content || !articleForm.category) {
      toast.error('Please fill in title, content, and category');
      return;
    }

    setSavingArticle(true);
    try {
      const payload = {
        title: articleForm.title,
        slug: articleForm.slug || generateSlug(articleForm.title),
        content: articleForm.content,
        excerpt: articleForm.excerpt || articleForm.content.substring(0, 200) + '...',
        category: articleForm.category,
        featured_image: articleForm.featured_image || null,
        tags: articleForm.tags ? articleForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      if (editingArticle) {
        await axios.put(`${API}/admin/articles/${editingArticle.article_id}`, payload);
        toast.success('Article updated successfully');
      } else {
        await axios.post(`${API}/admin/articles`, payload);
        toast.success('Article created successfully');
      }
      
      setShowArticleDialog(false);
      refreshCurrentTab();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save article');
    } finally {
      setSavingArticle(false);
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`);
      toast.success('Article deleted');
      refreshCurrentTab();
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  const handleTogglePublish = async (articleId) => {
    try {
      const response = await axios.put(`${API}/admin/articles/${articleId}/toggle-publish`);
      toast.success(response.data.message);
      refreshCurrentTab();
    } catch (error) {
      toast.error('Failed to toggle publish status');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-heading font-bold">Admin Dashboard</h1>
                <p className="text-muted-foreground">Content moderation & community management</p>
              </div>
            </div>

            {/* Stats Cards */}
            {stats && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Flag className="w-5 h-5 text-destructive" />
                    <span className="text-sm text-muted-foreground">Pending Reports</span>
                  </div>
                  <div className="text-3xl font-bold">{stats.pending_reports}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Briefcase className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-muted-foreground">Pending Jobs</span>
                  </div>
                  <div className="text-3xl font-bold">{moderationStats?.jobs?.pending || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Building className="w-5 h-5 text-amber-600" />
                    <span className="text-sm text-muted-foreground">Pending Businesses</span>
                  </div>
                  <div className="text-3xl font-bold">{moderationStats?.businesses?.pending || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Home className="w-5 h-5 text-teal-600" />
                    <span className="text-sm text-muted-foreground">Pending Properties</span>
                  </div>
                  <div className="text-3xl font-bold">{moderationStats?.properties?.pending || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <ShoppingBag className="w-5 h-5 text-pink-600" />
                    <span className="text-sm text-muted-foreground">Pending Marketplace</span>
                  </div>
                  <div className="text-3xl font-bold">{moderationStats?.marketplace?.pending || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Total Users</span>
                  </div>
                  <div className="text-3xl font-bold">{stats.users || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Calendar className="w-5 h-5 text-primary" />
                    <span className="text-sm text-muted-foreground">Total Events</span>
                  </div>
                  <div className="text-3xl font-bold">{stats.events || 0}</div>
                </div>
                <div className="bg-white rounded-2xl p-6 border-2 border-border">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-purple-600" />
                    <span className="text-sm text-muted-foreground">Total Groups</span>
                  </div>
                  <div className="text-3xl font-bold">{stats.groups || 0}</div>
                  {stats.private_groups > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">({stats.private_groups} private)</p>
                  )}
                </div>
              </div>
            )}

            {/* Tabs */}
            <Tabs defaultValue="jobs" value={activeTab} onValueChange={setActiveTab} className="space-y-6">
              <TabsList className="bg-white border-2 border-border p-1 rounded-xl flex flex-wrap gap-1 h-auto">
                <TabsTrigger value="events" className="rounded-lg">
                  <Calendar className="w-4 h-4 mr-1" />
                  Current Events {loadedTabs.events ? `(${currentEvents.length})` : `(${stats?.events || 0})`}
                </TabsTrigger>
                <TabsTrigger value="past-events" className="rounded-lg">
                  <Calendar className="w-4 h-4 mr-1 opacity-50" />
                  Past Events {loadedTabs.events ? `(${pastEvents.length})` : ''}
                </TabsTrigger>
                <TabsTrigger value="jobs" className="rounded-lg">
                  <Briefcase className="w-4 h-4 mr-1" />
                  Jobs ({loadedTabs.jobs ? pendingJobs.length : (moderationStats?.pending_jobs || 0)}/{stats?.jobs || 0})
                </TabsTrigger>
                <TabsTrigger value="businesses" className="rounded-lg">
                  <Building className="w-4 h-4 mr-1" />
                  Businesses ({loadedTabs.businesses ? pendingBusinesses.length : (moderationStats?.pending_businesses || 0)}/{stats?.businesses || 0})
                </TabsTrigger>
                <TabsTrigger value="properties" className="rounded-lg">
                  <Home className="w-4 h-4 mr-1" />
                  Housing ({loadedTabs.properties ? pendingProperties.length : (moderationStats?.pending_properties || 0)}/{stats?.properties || 0})
                </TabsTrigger>
                <TabsTrigger value="marketplace" className="rounded-lg">
                  <ShoppingBag className="w-4 h-4 mr-1" />
                  Marketplace ({loadedTabs.marketplace ? pendingMarketplace.length : (moderationStats?.pending_marketplace || 0)}/{stats?.marketplace_listings || 0})
                </TabsTrigger>
                <TabsTrigger value="reports" className="rounded-lg">
                  User Reports ({stats?.pending_reports || 0})
                </TabsTrigger>
                <TabsTrigger value="articles" className="rounded-lg">
                  <BookOpen className="w-4 h-4 mr-1" />
                  Guides {loadedTabs.articles ? `(${articles.length})` : ''}
                </TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg">
                  <Users className="w-4 h-4 mr-1" />
                  Members ({stats?.users || 0})
                </TabsTrigger>
                <TabsTrigger value="permissions" className="rounded-lg">
                  <UserPlus className="w-4 h-4 mr-1" />
                  Permissions
                </TabsTrigger>
                <TabsTrigger value="groups" className="rounded-lg">
                  <Users className="w-4 h-4 mr-1" />
                  Groups ({stats?.groups || 0})
                </TabsTrigger>
                <TabsTrigger value="tools" className="rounded-lg">
                  <Settings className="w-4 h-4 mr-1" />
                  Tools
                </TabsTrigger>
                <TabsTrigger value="polls" className="rounded-lg" data-testid="admin-tab-polls">
                  <BarChart3 className="w-4 h-4 mr-1" />
                  Polls
                </TabsTrigger>
                <TabsTrigger value="trash" className="rounded-lg data-[state=active]:bg-red-500 data-[state=active]:text-white" data-testid="admin-tab-trash">
                  <Trash2 className="w-4 h-4 mr-1" />
                  Deleted Events
                </TabsTrigger>
              </TabsList>

              {/* Current Events Management Tab */}
              <TabsContent value="events" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Current Events</h2>
                  <Link to="/create-event">
                    <Button className="rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Event
                    </Button>
                  </Link>
                </div>

                {currentEvents.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No upcoming events</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {currentEvents.map((event) => (
                      <div key={event.event_id} className="bg-white rounded-xl p-4 border-2 border-border">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex gap-4 flex-1 min-w-0">
                            {event.image_url && (
                              <img 
                                src={event.image_url} 
                                alt={event.title}
                                className="w-20 h-20 rounded-lg object-cover flex-shrink-0"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-lg truncate">{event.title}</h3>
                              <p className="text-sm text-muted-foreground">{event.city}, {event.country}</p>
                              <p className="text-sm text-muted-foreground">
                                {event.event_date} {event.group_name ? <span className="text-primary font-medium">&middot; {event.group_name}</span> : event.category ? <span className="text-muted-foreground">&middot; {event.category}</span> : null}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Created by: {event.creator_name || 'System'}
                                {event.is_invite_only && <span className="ml-2 text-purple-600 font-semibold">• Invite Only</span>}
                              </p>
                              {/* Inline group assignment */}
                              <div className="mt-2">
                                <Select
                                  value={event.group_slug || 'none'}
                                  onValueChange={async (newSlug) => {
                                    const slug = newSlug === 'none' ? null : newSlug;
                                    setActionLoading(`assign-group-${event.event_id}`);
                                    try {
                                      const res = await axios.patch(`${API}/admin/events/${event.event_id}/group`, { group_slug: slug });
                                      toast.success(res.data.message);
                                      refreshCurrentTab();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to update group');
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                  disabled={actionLoading === `assign-group-${event.event_id}`}
                                >
                                  <SelectTrigger className="h-8 text-xs w-full max-w-[200px] rounded-lg" data-testid={`event-group-select-${event.event_id}`}>
                                    <SelectValue placeholder="Assign group..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No group</SelectItem>
                                    {allGroups.map((g) => (
                                      <SelectItem key={g.slug} value={g.slug}>{g.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0 flex-wrap justify-end">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={async () => {
                                const target = event.group_slug ? 'group members' : 'all members in the same country';
                                if (!window.confirm(`Send notification for "${event.title}" to ${target}?`)) return;
                                setActionLoading(`notify-${event.event_id}`);
                                try {
                                  const res = await axios.post(`${API}/admin/events/${event.event_id}/resend-notification`);
                                  toast.success(res.data.message);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to send notifications');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                              disabled={actionLoading === `notify-${event.event_id}`}
                              data-testid={`resend-notify-${event.event_id}`}
                              title="Send notification"
                            >
                              {actionLoading === `notify-${event.event_id}` ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant={event.is_invite_only ? "default" : "outline"}
                              size="sm"
                              className={`rounded-full ${event.is_invite_only ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
                              onClick={() => handleToggleInviteOnly(event)}
                              disabled={actionLoading === event.event_id}
                              title={event.is_invite_only ? "Make Open to All" : "Make Invite Only"}
                            >
                              {actionLoading === event.event_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : event.is_invite_only ? (
                                <Lock className="w-4 h-4" />
                              ) : (
                                <Unlock className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full"
                              onClick={() => openEventImageDialog(event)}
                              title="Edit Image"
                            >
                              <Image className="w-4 h-4" />
                            </Button>
                            <Link to={`/events/${event.event_id}`}>
                              <Button variant="outline" size="sm" className="rounded-full">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="rounded-full"
                              onClick={() => handleDeleteEvent(event.event_id, event.title)}
                              disabled={actionLoading === event.event_id}
                            >
                              {actionLoading === event.event_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Past Events Tab */}
              <TabsContent value="past-events" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Past Events</h2>
                </div>

                {pastEvents.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No past events</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pastEvents.map((event) => (
                      <div key={event.event_id} className="bg-white rounded-xl p-4 border-2 border-border opacity-75">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex gap-4 flex-1 min-w-0">
                            {event.image_url && (
                              <img 
                                src={event.image_url} 
                                alt={event.title}
                                className="w-20 h-20 rounded-lg object-cover flex-shrink-0 grayscale"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <h3 className="font-bold text-lg truncate">{event.title}</h3>
                              <p className="text-sm text-muted-foreground">{event.city}, {event.country}</p>
                              <p className="text-sm text-muted-foreground">
                                {event.event_date} {event.group_name ? <span className="text-primary font-medium">&middot; {event.group_name}</span> : event.category ? <span className="text-muted-foreground">&middot; {event.category}</span> : null}
                              </p>
                              <p className="text-xs text-muted-foreground mt-1">
                                Created by: {event.creator_name || 'System'}
                                <span className="ml-2 text-amber-600 font-semibold">• Past Event</span>
                              </p>
                              <div className="mt-2">
                                <Select
                                  value={event.group_slug || 'none'}
                                  onValueChange={async (newSlug) => {
                                    const slug = newSlug === 'none' ? null : newSlug;
                                    setActionLoading(`assign-group-${event.event_id}`);
                                    try {
                                      const res = await axios.patch(`${API}/admin/events/${event.event_id}/group`, { group_slug: slug });
                                      toast.success(res.data.message);
                                      refreshCurrentTab();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to update group');
                                    } finally {
                                      setActionLoading(null);
                                    }
                                  }}
                                  disabled={actionLoading === `assign-group-${event.event_id}`}
                                >
                                  <SelectTrigger className="h-8 text-xs w-full max-w-[200px] rounded-lg" data-testid={`past-event-group-select-${event.event_id}`}>
                                    <SelectValue placeholder="Assign group..." />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="none">No group</SelectItem>
                                    {allGroups.map((g) => (
                                      <SelectItem key={g.slug} value={g.slug}>{g.name}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              className="rounded-full text-blue-600 border-blue-200 hover:bg-blue-50"
                              onClick={async () => {
                                const target = event.group_slug ? 'group members' : 'all members in the same country';
                                if (!window.confirm(`Send notification for "${event.title}" to ${target}?`)) return;
                                setActionLoading(`notify-${event.event_id}`);
                                try {
                                  const res = await axios.post(`${API}/admin/events/${event.event_id}/resend-notification`);
                                  toast.success(res.data.message);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                              disabled={actionLoading === `notify-${event.event_id}`}
                              data-testid={`resend-past-notify-${event.event_id}`}
                            >
                              {actionLoading === `notify-${event.event_id}` ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Mail className="w-4 h-4" />
                              )}
                            </Button>
                            <Link to={`/events/${event.event_id}`}>
                              <Button variant="outline" size="sm" className="rounded-full">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button
                              variant="destructive"
                              size="sm"
                              className="rounded-full"
                              onClick={() => handleDeleteEvent(event.event_id, event.title)}
                              disabled={actionLoading === event.event_id}
                            >
                              {actionLoading === event.event_id ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Trash2 className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Pending Jobs Tab */}
              <TabsContent value="jobs" className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-xl font-bold">Job Listings</h2>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                      data-testid="delete-all-jobs-btn"
                      disabled={actionLoading === 'delete-all-listings'}
                      onClick={async () => {
                        if (!window.confirm('Delete ALL listings across Jobs, Housing, Marketplace, and Businesses?')) return;
                        if (!window.confirm('FINAL confirmation. All listings will be permanently deleted.')) return;
                        setActionLoading('delete-all-listings');
                        try {
                          const res = await axios.delete(`${API}/admin/delete-all-listings`);
                          const d = res.data.details;
                          toast.success(`Deleted: ${d.jobs} jobs, ${d.housing} housing, ${d.marketplace} marketplace, ${d.businesses} businesses`);
                          refreshCurrentTab();
                          refreshCurrentTab();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed');
                        } finally { setActionLoading(null); }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All Listings
                    </Button>
                    <Select value={jobsFilter} onValueChange={setJobsFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending ({pendingJobs.length})</SelectItem>
                        <SelectItem value="all">All Jobs ({allJobs.length})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {jobsFilter === 'pending' ? (
                  pendingJobs.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-muted-foreground">No pending job listings to review</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingJobs.map(job => (
                        <div key={job.job_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                  {job.category}
                                </span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  {job.job_type}
                                </span>
                                {job.visa_sponsorship && (
                                  <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                    Visa Sponsorship
                                  </span>
                                )}
                              </div>
                              <h3 className="font-bold text-lg">{job.title}</h3>
                              <p className="text-sm text-muted-foreground">{job.company_name} • {job.city}, {job.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Posted by: {job.poster_name}</p>
                              <p className="text-sm mt-3 line-clamp-3">{job.description}</p>
                              {job.salary_min && (
                                <p className="text-sm text-green-600 font-medium mt-2">
                                  Salary: {job.salary_currency} {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()} / {job.salary_period}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button 
                                size="sm" 
                                onClick={() => handleJobModeration(job.job_id, 'approve')}
                                disabled={actionLoading === job.job_id}
                                className="bg-green-500 hover:bg-green-600"
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approve
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => {
                                  const reason = window.prompt('Reason for rejection (optional):');
                                  handleJobModeration(job.job_id, 'reject', reason);
                                }}
                                disabled={actionLoading === job.job_id}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Reject
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDeleteJob(job.job_id, job.title)}
                                disabled={actionLoading === job.job_id}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  allJobs.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No job listings found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {allJobs.map(job => (
                        <div key={job.job_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                  {job.category}
                                </span>
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                  {job.job_type}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  job.moderation_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                                }`}>
                                  {job.moderation_status || 'approved'}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{job.title}</h3>
                              <p className="text-sm text-muted-foreground">{job.company_name} • {job.city}, {job.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Posted by: {job.poster_name}</p>
                              {job.salary_min && (
                                <p className="text-sm text-green-600 font-medium mt-2">
                                  Salary: {job.salary_currency} {job.salary_min?.toLocaleString()} - {job.salary_max?.toLocaleString()} / {job.salary_period}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleDeleteJob(job.job_id, job.title)}
                                disabled={actionLoading === job.job_id}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </TabsContent>

              {/* Pending Businesses Tab */}
              <TabsContent value="businesses" className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-xl font-bold">Business Listings</h2>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                      data-testid="delete-all-businesses-btn"
                      disabled={actionLoading === 'delete-all-listings'}
                      onClick={async () => {
                        if (!window.confirm('Delete ALL listings across Jobs, Housing, Marketplace, and Businesses?')) return;
                        if (!window.confirm('FINAL confirmation. All listings will be permanently deleted.')) return;
                        setActionLoading('delete-all-listings');
                        try {
                          const res = await axios.delete(`${API}/admin/delete-all-listings`);
                          const d = res.data.details;
                          toast.success(`Deleted: ${d.jobs} jobs, ${d.housing} housing, ${d.marketplace} marketplace, ${d.businesses} businesses`);
                          refreshCurrentTab();
                          refreshCurrentTab();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed');
                        } finally { setActionLoading(null); }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All Listings
                    </Button>
                    <Select value={businessesFilter} onValueChange={setBusinessesFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending ({pendingBusinesses.length})</SelectItem>
                        <SelectItem value="all">All ({allBusinesses.length})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {businessesFilter === 'pending' ? (
                  pendingBusinesses.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-muted-foreground">No pending business listings to review</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingBusinesses.map(business => (
                        <div key={business.business_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                  {business.category}
                                </span>
                                {business.is_expat_owned && (
                                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                    Expat Owned
                                  </span>
                                )}
                              </div>
                              <h3 className="font-bold text-lg">{business.name}</h3>
                              <p className="text-sm text-muted-foreground">{business.city}, {business.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Registered by: {business.owner_name}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" onClick={() => handleBusinessModeration(business.business_id, 'approve')} disabled={actionLoading === business.business_id} className="bg-green-500 hover:bg-green-600">
                                <CheckCircle className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { const reason = window.prompt('Reason for rejection (optional):'); handleBusinessModeration(business.business_id, 'reject', reason); }} disabled={actionLoading === business.business_id}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteBusiness(business.business_id, business.name)} disabled={actionLoading === business.business_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  allBusinesses.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <Building className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No businesses found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {allBusinesses.map(business => (
                        <div key={business.business_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                  {business.category}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${business.moderation_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {business.moderation_status || 'approved'}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{business.name}</h3>
                              <p className="text-sm text-muted-foreground">{business.city}, {business.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Owner: {business.owner_name}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" variant="outline" onClick={() => handleDeleteBusiness(business.business_id, business.name)} disabled={actionLoading === business.business_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4 mr-1" />Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </TabsContent>

              {/* Pending Properties Tab */}
              <TabsContent value="properties" className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-xl font-bold">Property Listings</h2>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                      data-testid="delete-all-housing-btn"
                      disabled={actionLoading === 'delete-all-listings'}
                      onClick={async () => {
                        if (!window.confirm('Delete ALL listings across Jobs, Housing, Marketplace, and Businesses?')) return;
                        if (!window.confirm('FINAL confirmation. All listings will be permanently deleted.')) return;
                        setActionLoading('delete-all-listings');
                        try {
                          const res = await axios.delete(`${API}/admin/delete-all-listings`);
                          const d = res.data.details;
                          toast.success(`Deleted: ${d.jobs} jobs, ${d.housing} housing, ${d.marketplace} marketplace, ${d.businesses} businesses`);
                          refreshCurrentTab();
                          refreshCurrentTab();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed');
                        } finally { setActionLoading(null); }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All Listings
                    </Button>
                    <Select value={propertiesFilter} onValueChange={setPropertiesFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending ({pendingProperties.length})</SelectItem>
                        <SelectItem value="all">All ({allProperties.length})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {propertiesFilter === 'pending' ? (
                  pendingProperties.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-muted-foreground">No pending property listings to review</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingProperties.map(property => (
                        <div key={property.property_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">{property.property_type}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${property.listing_type === 'sale' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                  {property.listing_type === 'sale' ? 'For Sale' : 'For Rent'}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{property.title}</h3>
                              <p className="text-sm text-muted-foreground">{property.city}, {property.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Listed by: {property.owner_name}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" onClick={() => handlePropertyModeration(property.property_id, 'approve')} disabled={actionLoading === property.property_id} className="bg-green-500 hover:bg-green-600">
                                <CheckCircle className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { const reason = window.prompt('Reason for rejection (optional):'); handlePropertyModeration(property.property_id, 'reject', reason); }} disabled={actionLoading === property.property_id}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteProperty(property.property_id, property.title)} disabled={actionLoading === property.property_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  allProperties.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <Home className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No properties found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {allProperties.map(property => (
                        <div key={property.property_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">{property.property_type}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${property.moderation_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {property.moderation_status || 'approved'}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{property.title}</h3>
                              <p className="text-sm text-muted-foreground">{property.city}, {property.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Owner: {property.owner_name}</p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" variant="outline" onClick={() => handleDeleteProperty(property.property_id, property.title)} disabled={actionLoading === property.property_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4 mr-1" />Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </TabsContent>

              {/* Pending Marketplace Tab */}
              <TabsContent value="marketplace" className="space-y-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  <h2 className="text-xl font-bold">Marketplace Listings</h2>
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 border-red-200 hover:bg-red-50 rounded-full"
                      data-testid="delete-all-marketplace-btn"
                      disabled={actionLoading === 'delete-all-listings'}
                      onClick={async () => {
                        if (!window.confirm('Delete ALL listings across Jobs, Housing, Marketplace, and Businesses?')) return;
                        if (!window.confirm('FINAL confirmation. All listings will be permanently deleted.')) return;
                        setActionLoading('delete-all-listings');
                        try {
                          const res = await axios.delete(`${API}/admin/delete-all-listings`);
                          const d = res.data.details;
                          toast.success(`Deleted: ${d.jobs} jobs, ${d.housing} housing, ${d.marketplace} marketplace, ${d.businesses} businesses`);
                          refreshCurrentTab();
                          refreshCurrentTab();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Failed');
                        } finally { setActionLoading(null); }
                      }}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete All Listings
                    </Button>
                    <Select value={marketplaceFilter} onValueChange={setMarketplaceFilter}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending ({pendingMarketplace.length})</SelectItem>
                        <SelectItem value="all">All ({allMarketplaceListings.length})</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {marketplaceFilter === 'pending' ? (
                  pendingMarketplace.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                      <p className="text-muted-foreground">No pending marketplace listings to review</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {pendingMarketplace.map(listing => (
                        <div key={listing.listing_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">{listing.category}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${listing.condition === 'New' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {listing.condition}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{listing.title}</h3>
                              <p className="text-sm text-muted-foreground">{listing.city}, {listing.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Seller: {listing.seller_name}</p>
                              <p className="text-lg font-bold text-pink-600 mt-2">
                                {listing.is_free ? 'FREE' : new Intl.NumberFormat('en-US', { style: 'currency', currency: listing.price_currency || 'USD', maximumFractionDigits: 0 }).format(listing.price)}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" onClick={() => handleMarketplaceModeration(listing.listing_id, 'approve')} disabled={actionLoading === listing.listing_id} className="bg-green-500 hover:bg-green-600">
                                <CheckCircle className="w-4 h-4 mr-1" />Approve
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => { const reason = window.prompt('Reason for rejection (optional):'); handleMarketplaceModeration(listing.listing_id, 'reject', reason); }} disabled={actionLoading === listing.listing_id}>
                                <XCircle className="w-4 h-4 mr-1" />Reject
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleDeleteMarketplaceListing(listing.listing_id, listing.title)} disabled={actionLoading === listing.listing_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  allMarketplaceListings.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                      <ShoppingBag className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No marketplace listings found</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {allMarketplaceListings.map(listing => (
                        <div key={listing.listing_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                          <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4">
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2 py-1 bg-pink-100 text-pink-700 rounded-full text-xs font-medium">{listing.category}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${listing.moderation_status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {listing.moderation_status || 'approved'}
                                </span>
                              </div>
                              <h3 className="font-bold text-lg">{listing.title}</h3>
                              <p className="text-sm text-muted-foreground">{listing.city}, {listing.country}</p>
                              <p className="text-sm text-muted-foreground mt-2">Seller: {listing.seller_name}</p>
                              <p className="text-lg font-bold text-pink-600 mt-2">
                                {listing.is_free ? 'FREE' : new Intl.NumberFormat('en-US', { style: 'currency', currency: listing.price_currency || 'USD', maximumFractionDigits: 0 }).format(listing.price)}
                              </p>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button size="sm" variant="outline" onClick={() => handleDeleteMarketplaceListing(listing.listing_id, listing.title)} disabled={actionLoading === listing.listing_id} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="w-4 h-4 mr-1" />Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </TabsContent>

              {/* User Reports Tab */}
              <TabsContent value="reports" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">User Reports</h2>
                  <Select value={reportFilter} onValueChange={setReportFilter}>
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="dismissed">Dismissed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {reports.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-muted-foreground">No {reportFilter} reports</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map((report) => (
                      <div key={report.report_id} className="bg-white rounded-2xl p-6 border-2 border-border">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                report.reason === 'sexual' ? 'bg-red-100 text-red-700' :
                                report.reason === 'harassment' ? 'bg-orange-100 text-orange-700' :
                                report.reason === 'spam' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {report.reason}
                              </span>
                              <span className="text-sm text-muted-foreground">
                                {report.content_type} • Reported by {report.reporter_name}
                              </span>
                            </div>
                            {report.content_details && (
                              <div className="bg-muted/50 rounded-lg p-4 mb-3">
                                <p className="font-semibold">{report.content_details.title || report.content_details.name}</p>
                                <p className="text-sm text-muted-foreground line-clamp-2">
                                  {report.content_details.description || report.content_details.text || report.content_details.email}
                                </p>
                              </div>
                            )}
                            {report.description && (
                              <p className="text-sm text-muted-foreground italic">"{report.description}"</p>
                            )}
                          </div>
                          {report.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleReportAction(report.report_id, 'dismiss')}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Dismiss
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReportAction(report.report_id, 'delete_content')}
                              >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Delete
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleReportAction(report.report_id, 'ban_user')}
                              >
                                <Ban className="w-4 h-4 mr-1" />
                                Ban
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* AI Flagged Tab */}
              <TabsContent value="ai-flagged" className="space-y-4">
                <h2 className="text-xl font-bold">AI-Flagged Content</h2>
                
                {moderationQueue.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
                    <p className="text-muted-foreground">No content pending review</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {moderationQueue.map((item) => (
                      <div key={item.content_id} className="bg-white rounded-2xl p-6 border-2 border-amber-200 bg-amber-50/50">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500" />
                              <span className="font-semibold">Event: {item.title}</span>
                            </div>
                            <div className="flex flex-wrap gap-2 mb-3">
                              {item.flagged_reasons?.map((reason) => (
                                <span key={reason} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                  {reason}
                                </span>
                              ))}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Created by {item.creator_name}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleModerationAction(item.content_id, 'approve')}
                              className="text-green-600 hover:bg-green-50"
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleModerationAction(item.content_id, 'delete')}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Articles/Guides Management Tab */}
              <TabsContent value="articles" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Manage Expat Guides</h2>
                  <Button onClick={openNewArticle} className="rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    New Article
                  </Button>
                </div>

                {articles.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No articles yet</p>
                    <Button onClick={openNewArticle} className="rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Create Your First Article
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {articles.map((article) => (
                      <div key={article.article_id} className={`bg-white rounded-2xl p-6 border-2 ${article.published ? 'border-border' : 'border-amber-200 bg-amber-50/30'}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-lg">{ARTICLE_CATEGORIES.find(c => c.id === article.category)?.icon || '📄'}</span>
                              <span className="px-3 py-1 rounded-full text-xs font-bold bg-primary/10 text-primary">
                                {ARTICLE_CATEGORIES.find(c => c.id === article.category)?.name || article.category}
                              </span>
                              {!article.published && (
                                <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                  Draft
                                </span>
                              )}
                              <span className="text-sm text-muted-foreground">
                                {article.views} views
                              </span>
                            </div>
                            <h3 className="font-bold text-lg mb-1">{article.title}</h3>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                              {article.excerpt}
                            </p>
                            <div className="flex flex-wrap gap-1">
                              {article.tags?.map((tag) => (
                                <span key={tag} className="px-2 py-0.5 bg-muted rounded text-xs">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditArticle(article)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant={article.published ? "ghost" : "outline"}
                              onClick={() => handleTogglePublish(article.article_id)}
                              className={article.published ? "text-amber-600" : "text-green-600"}
                            >
                              {article.published ? (
                                <>
                                  <EyeOff className="w-4 h-4 mr-1" />
                                  Unpublish
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 mr-1" />
                                  Publish
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteArticle(article.article_id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Members Tab */}
              <TabsContent value="users" className="space-y-4">
                {(() => {
                  const getLastName = (name) => {
                    if (!name) return '';
                    const parts = name.trim().split(/\s+/);
                    return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
                  };
                  
                  const isMemberActive = (u) => {
                    if (!u.membership_expires) return false;
                    try {
                      return new Date(u.membership_expires) > new Date();
                    } catch { return false; }
                  };

                  const activeCount = users.filter(isMemberActive).length;
                  const expiredCount = users.length - activeCount;

                  // Filter
                  let filtered = users;
                  if (membersFilter === 'active') filtered = users.filter(isMemberActive);
                  else if (membersFilter === 'expired') filtered = users.filter(u => !isMemberActive(u));

                  // Search
                  if (membersSearch.trim()) {
                    const q = membersSearch.toLowerCase();
                    filtered = filtered.filter(u =>
                      (u.name || '').toLowerCase().includes(q) ||
                      (u.email || '').toLowerCase().includes(q) ||
                      (u.residence_city || '').toLowerCase().includes(q) ||
                      (u.residence_country || '').toLowerCase().includes(q) ||
                      (u.nationality || '').toLowerCase().includes(q)
                    );
                  }

                  // Sort
                  const sorted = [...filtered].sort((a, b) => {
                    let valA, valB;
                    switch (membersSort.field) {
                      case 'last_name':
                        valA = getLastName(a.name);
                        valB = getLastName(b.name);
                        break;
                      case 'city':
                        valA = (a.residence_city || '').toLowerCase();
                        valB = (b.residence_city || '').toLowerCase();
                        break;
                      case 'country':
                        valA = (a.residence_country || '').toLowerCase();
                        valB = (b.residence_country || '').toLowerCase();
                        break;
                      case 'joined':
                        valA = a.created_at || '';
                        valB = b.created_at || '';
                        break;
                      case 'status':
                        valA = isMemberActive(a) ? 0 : 1;
                        valB = isMemberActive(b) ? 0 : 1;
                        break;
                      default: // name
                        valA = (a.name || '').toLowerCase();
                        valB = (b.name || '').toLowerCase();
                    }
                    if (valA < valB) return membersSort.dir === 'asc' ? -1 : 1;
                    if (valA > valB) return membersSort.dir === 'asc' ? 1 : -1;
                    return 0;
                  });

                  const toggleSort = (field) => {
                    setMembersSort(prev =>
                      prev.field === field
                        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
                        : { field, dir: 'asc' }
                    );
                  };

                  const SortIcon = ({ field }) => {
                    if (membersSort.field !== field) return <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />;
                    return membersSort.dir === 'asc'
                      ? <ArrowUp className="w-3.5 h-3.5 text-primary" />
                      : <ArrowDown className="w-3.5 h-3.5 text-primary" />;
                  };

                  return (
                    <>
                      {/* Header with counts */}
                      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        <div>
                          <h2 className="text-xl font-bold" data-testid="members-heading">All Members</h2>
                          <p className="text-sm text-muted-foreground mt-0.5">
                            {users.length} total &middot; <span className="text-green-600">{activeCount} active</span> &middot; <span className="text-amber-600">{expiredCount} expired</span>
                          </p>
                        </div>
                        <Button size="sm" variant="outline" className="rounded-full" onClick={refreshCurrentTab} data-testid="members-refresh-btn">
                          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
                        </Button>
                      </div>

                      {/* Search + filter row */}
                      <div className="flex flex-col sm:flex-row gap-3">
                        <div className="relative flex-1">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                          <Input
                            placeholder="Search by name, email, city, country..."
                            value={membersSearch}
                            onChange={(e) => setMembersSearch(e.target.value)}
                            className="pl-9"
                            data-testid="members-search-input"
                          />
                          {membersSearch && (
                            <button onClick={() => setMembersSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                              <X className="w-4 h-4 text-gray-400 hover:text-gray-600" />
                            </button>
                          )}
                        </div>
                        <div className="flex gap-1.5">
                          {[
                            { key: 'all', label: 'All' },
                            { key: 'active', label: 'Active' },
                            { key: 'expired', label: 'Expired' },
                          ].map(f => (
                            <Button
                              key={f.key}
                              size="sm"
                              variant={membersFilter === f.key ? 'default' : 'outline'}
                              className="rounded-full text-xs"
                              onClick={() => setMembersFilter(f.key)}
                              data-testid={`members-filter-${f.key}`}
                            >
                              {f.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Results info */}
                      <p className="text-xs text-muted-foreground">
                        Showing {sorted.length} of {users.length} members
                        {membersSearch && <> matching &ldquo;{membersSearch}&rdquo;</>}
                      </p>

                      {sorted.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No members found</p>
                        </div>
                      ) : (
                        <>
                          {/* Desktop table */}
                          <div className="hidden md:block bg-white rounded-xl border-2 border-border overflow-hidden" data-testid="members-table">
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b bg-gray-50/80">
                                    <th className="text-left py-3 px-4 font-semibold">
                                      <button className="flex items-center gap-1.5 hover:text-primary transition-colors" onClick={() => toggleSort('last_name')} data-testid="sort-last-name">
                                        Name <SortIcon field="last_name" />
                                      </button>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold">Email</th>
                                    <th className="text-left py-3 px-4 font-semibold">
                                      <button className="flex items-center gap-1.5 hover:text-primary transition-colors" onClick={() => toggleSort('city')} data-testid="sort-city">
                                        <MapPin className="w-3.5 h-3.5" /> City <SortIcon field="city" />
                                      </button>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold">
                                      <button className="flex items-center gap-1.5 hover:text-primary transition-colors" onClick={() => toggleSort('country')} data-testid="sort-country">
                                        <Globe className="w-3.5 h-3.5" /> Country <SortIcon field="country" />
                                      </button>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold">
                                      <button className="flex items-center gap-1.5 hover:text-primary transition-colors" onClick={() => toggleSort('status')} data-testid="sort-status">
                                        Status <SortIcon field="status" />
                                      </button>
                                    </th>
                                    <th className="text-left py-3 px-4 font-semibold">
                                      <button className="flex items-center gap-1.5 hover:text-primary transition-colors" onClick={() => toggleSort('joined')} data-testid="sort-joined">
                                        Joined <SortIcon field="joined" />
                                      </button>
                                    </th>
                                    <th className="text-right py-3 px-4 font-semibold">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {sorted.map((u, i) => (
                                    <tr key={u.user_id} className={`border-b last:border-0 hover:bg-gray-50/60 transition-colors ${i % 2 === 0 ? '' : 'bg-gray-50/30'}`} data-testid={`member-row-${u.user_id}`}>
                                      <td className="py-2.5 px-4">
                                        <Link to={`/profile/${u.user_id}`} className="flex items-center gap-2.5 group">
                                          <span className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                                          </span>
                                          <span className="font-medium group-hover:text-primary transition-colors truncate max-w-[160px]">{u.name || 'Unknown'}</span>
                                        </Link>
                                      </td>
                                      <td className="py-2.5 px-4 text-muted-foreground truncate max-w-[180px]">{u.email}</td>
                                      <td className="py-2.5 px-4">{u.residence_city || <span className="text-gray-300">—</span>}</td>
                                      <td className="py-2.5 px-4">{u.residence_country || <span className="text-gray-300">—</span>}</td>
                                      <td className="py-2.5 px-4">
                                        {u.is_banned ? (
                                          <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">Banned</span>
                                        ) : isMemberActive(u) ? (
                                          <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                                        ) : (
                                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">Expired</span>
                                        )}
                                      </td>
                                      <td className="py-2.5 px-4 text-muted-foreground text-xs">
                                        {u.created_at ? new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                                      </td>
                                      <td className="py-2.5 px-4 text-right">
                                        <div className="flex items-center justify-end gap-1.5">
                                          <Link to={`/profile/${u.user_id}`}>
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`view-member-${u.user_id}`}>
                                              <Eye className="w-3.5 h-3.5" />
                                            </Button>
                                          </Link>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                            data-testid={`delete-member-${u.user_id}`}
                                            onClick={async () => {
                                              if (window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) {
                                                try {
                                                  await axios.delete(`${API}/admin/users/${u.user_id}`);
                                                  toast.success(`User ${u.name} deleted`);
                                                  refreshCurrentTab();
                                                } catch (error) {
                                                  toast.error(error.response?.data?.detail || 'Failed to delete user');
                                                }
                                              }
                                            }}
                                          >
                                            <Trash2 className="w-3.5 h-3.5" />
                                          </Button>
                                        </div>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>

                          {/* Mobile card list */}
                          <div className="md:hidden space-y-2" data-testid="members-mobile-list">
                            {/* Mobile sort buttons */}
                            <div className="flex gap-1.5 flex-wrap mb-2">
                              <span className="text-xs text-muted-foreground self-center mr-1">Sort:</span>
                              {[
                                { key: 'last_name', label: 'Name' },
                                { key: 'city', label: 'City' },
                                { key: 'country', label: 'Country' },
                                { key: 'joined', label: 'Joined' },
                              ].map(s => (
                                <Button
                                  key={s.key}
                                  size="sm"
                                  variant={membersSort.field === s.key ? 'default' : 'outline'}
                                  className="rounded-full text-xs h-7 px-2.5"
                                  onClick={() => toggleSort(s.key)}
                                  data-testid={`mobile-sort-${s.key}`}
                                >
                                  {s.label}
                                  {membersSort.field === s.key && (
                                    membersSort.dir === 'asc' ? <ArrowUp className="w-3 h-3 ml-0.5" /> : <ArrowDown className="w-3 h-3 ml-0.5" />
                                  )}
                                </Button>
                              ))}
                            </div>

                            {sorted.map((u) => (
                              <div key={u.user_id} className="bg-white rounded-xl p-3.5 border border-border" data-testid={`member-card-${u.user_id}`}>
                                <div className="flex items-start justify-between gap-2">
                                  <Link to={`/profile/${u.user_id}`} className="flex items-center gap-2.5 min-w-0 flex-1">
                                    <span className="w-9 h-9 bg-primary/10 rounded-full flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                                      {u.name?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                    <div className="min-w-0">
                                      <p className="font-semibold text-sm truncate">{u.name || 'Unknown'}</p>
                                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                                    </div>
                                  </Link>
                                  <div className="flex items-center gap-1.5 flex-shrink-0">
                                    {u.is_banned ? (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700 font-medium">Banned</span>
                                    ) : isMemberActive(u) ? (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                                    ) : (
                                      <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700 font-medium">Expired</span>
                                    )}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`mobile-delete-member-${u.user_id}`}
                                      onClick={async (e) => {
                                        e.preventDefault();
                                        if (window.confirm(`Delete user "${u.name}"? This cannot be undone.`)) {
                                          try {
                                            await axios.delete(`${API}/admin/users/${u.user_id}`);
                                            toast.success(`User ${u.name} deleted`);
                                            refreshCurrentTab();
                                          } catch (error) {
                                            toast.error(error.response?.data?.detail || 'Failed to delete user');
                                          }
                                        }
                                      }}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-2 text-xs text-muted-foreground pl-[46px]">
                                  {u.residence_city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{u.residence_city}</span>}
                                  {u.residence_country && <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{u.residence_country}</span>}
                                  {u.nationality && <span>{u.nationality}</span>}
                                  {u.created_at && <span>Joined {new Date(u.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  );
                })()}
              </TabsContent>

              {/* Permissions Tab */}
              <TabsContent value="permissions" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Permission Management</h2>
                </div>

                {/* Pending Permission Requests */}
                {permissionRequests.filter(r => r.status === 'pending').length > 0 && (
                  <div className="bg-amber-50 rounded-xl border-2 border-amber-200 p-6">
                    <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                      <Clock className="w-5 h-5 text-amber-600" />
                      Pending Requests ({permissionRequests.filter(r => r.status === 'pending').length})
                    </h3>
                    <div className="space-y-3">
                      {permissionRequests.filter(r => r.status === 'pending').map(request => (
                        <div key={request.user_id} className="flex flex-col p-4 bg-white rounded-lg border gap-3">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="font-medium">{request.user_name}</p>
                              <p className="text-sm text-gray-500 truncate">{request.user_email}</p>
                              {request.reason && (
                                <p className="text-sm text-gray-600 mt-1 italic">"{request.reason}"</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                Requested: {new Date(request.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div>
                            <textarea
                              id={`reply-${request.user_id}`}
                              className="w-full text-sm border rounded-lg p-2 resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
                              placeholder="Write a reply to the member (optional)..."
                              rows={2}
                              data-testid={`permission-reply-${request.user_id}`}
                            />
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              disabled={actionLoading === `approve-${request.user_id}`}
                              data-testid={`permission-approve-${request.user_id}`}
                              onClick={async () => {
                                const reply = document.getElementById(`reply-${request.user_id}`)?.value || '';
                                setActionLoading(`approve-${request.user_id}`);
                                try {
                                  await axios.post(`${API}/admin/approve-permission-request`, { user_id: request.user_id, reply });
                                  toast.success(`Permission granted to ${request.user_name}`);
                                  refreshCurrentTab();
                                } catch (error) {
                                  toast.error('Failed to approve request');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={actionLoading === `deny-${request.user_id}`}
                              data-testid={`permission-deny-${request.user_id}`}
                              onClick={async () => {
                                const reply = document.getElementById(`reply-${request.user_id}`)?.value || '';
                                const reason = window.prompt('Reason for denial (optional):');
                                setActionLoading(`deny-${request.user_id}`);
                                try {
                                  await axios.post(`${API}/admin/deny-permission-request`, { user_id: request.user_id, reason, reply });
                                  toast.success('Request denied');
                                  refreshCurrentTab();
                                } catch (error) {
                                  toast.error('Failed to deny request');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Deny
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Private Group Creators Section */}
                <div className="bg-white rounded-xl border-2 border-border p-6">
                  <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                    <Lock className="w-5 h-5 text-purple-500" />
                    Private Group Creators
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Users with permission to create private groups. They can then invite members and manage their own groups.
                  </p>

                  {/* Search to add user */}
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <Label className="text-sm font-medium mb-2 block">Grant Permission to User</Label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          placeholder="Search by name or email..."
                          value={userSearchQuery}
                          onChange={(e) => {
                            setUserSearchQuery(e.target.value);
                            if (e.target.value.length >= 2) {
                              const query = e.target.value.toLowerCase();
                              const results = users.filter(u => 
                                (u.name?.toLowerCase().includes(query) || u.email?.toLowerCase().includes(query)) &&
                                !privateGroupCreators.some(c => c.user_id === u.user_id)
                              ).slice(0, 5);
                              setUserSearchResults(results);
                            } else {
                              setUserSearchResults([]);
                            }
                          }}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    
                    {/* Search Results */}
                    {userSearchResults.length > 0 && (
                      <div className="mt-2 border rounded-lg divide-y">
                        {userSearchResults.map(u => (
                          <div key={u.user_id} className="flex items-center justify-between p-3 hover:bg-gray-50">
                            <div className="flex items-center gap-3">
                              {u.profile_picture ? (
                                <img src={u.profile_picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                  <span className="text-purple-600 text-sm font-medium">{u.name?.[0]}</span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-sm">{u.name}</p>
                                <p className="text-xs text-gray-500">{u.email}</p>
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700"
                              disabled={actionLoading === `grant-${u.user_id}`}
                              onClick={async () => {
                                setActionLoading(`grant-${u.user_id}`);
                                try {
                                  await axios.post(`${API}/admin/grant-private-group-permission`, { user_id: u.user_id });
                                  toast.success(`Granted permission to ${u.name}`);
                                  setUserSearchQuery('');
                                  setUserSearchResults([]);
                                  refreshCurrentTab();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to grant permission');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                            >
                              <UserPlus className="w-4 h-4 mr-1" />
                              Grant
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Current Creators List */}
                  <div>
                    <Label className="text-sm font-medium mb-2 block">
                      Users with Permission ({privateGroupCreators.length})
                    </Label>
                    {privateGroupCreators.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No users have been granted private group creation permission yet.</p>
                    ) : (
                      <div className="border rounded-lg divide-y">
                        {privateGroupCreators.map(creator => (
                          <div key={creator.user_id} className="flex items-center justify-between p-3">
                            <div className="flex items-center gap-3">
                              {creator.profile_picture ? (
                                <img src={creator.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                                  <span className="text-purple-600 font-medium">{creator.name?.[0]}</span>
                                </div>
                              )}
                              <div>
                                <p className="font-medium">{creator.name}</p>
                                <p className="text-sm text-gray-500">{creator.email}</p>
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={actionLoading === `revoke-${creator.user_id}`}
                              onClick={async () => {
                                if (!window.confirm(`Revoke private group creation permission from ${creator.name}?`)) return;
                                setActionLoading(`revoke-${creator.user_id}`);
                                try {
                                  await axios.post(`${API}/admin/revoke-private-group-permission`, { user_id: creator.user_id });
                                  toast.success(`Revoked permission from ${creator.name}`);
                                  refreshCurrentTab();
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || 'Failed to revoke permission');
                                } finally {
                                  setActionLoading(null);
                                }
                              }}
                            >
                              <UserMinus className="w-4 h-4 mr-1" />
                              Revoke
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </TabsContent>

              {/* Groups Management Tab */}
              <TabsContent value="groups" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">All Groups</h2>
                  <Link to="/groups">
                    <Button className="rounded-full">
                      View Groups Page
                    </Button>
                  </Link>
                </div>

                {allGroups.length === 0 ? (
                  <div className="bg-white rounded-2xl p-12 border-2 border-border text-center">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No groups yet</p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Private Groups Section - Always visible */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Lock className="w-4 h-4 text-purple-600" />
                        <h3 className="font-bold text-base text-purple-700">Private Groups ({allGroups.filter(g => g.is_private).length})</h3>
                      </div>
                      {allGroups.filter(g => g.is_private).length === 0 ? (
                        <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-center">
                          <p className="text-sm text-purple-600">No private groups have been created yet.</p>
                          <p className="text-xs text-muted-foreground mt-1">When a member with permission creates a private group, it will appear here.</p>
                        </div>
                      ) : (
                      <div className="space-y-3">
                        {allGroups.filter(g => g.is_private).map((group) => (
                      <div key={group.group_id} className="bg-white rounded-xl border-2 border-border overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-4 flex-1 min-w-0">
                              {group.image_url && (
                                <img 
                                  src={group.image_url} 
                                  alt={group.name}
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-bold text-lg truncate">{group.name}</h3>
                                  {group.is_private && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
                                      Private
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2">{group.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {group.member_count || 0} members{group.creator_name && group.creator_name !== 'Unknown' ? ` \u00B7 Created by ${group.creator_name}` : ''}
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => toggleGroupExpanded(group.slug)}
                                data-testid={`toggle-members-${group.slug}`}
                                title={expandedGroups[group.slug] ? 'Hide members' : 'Show members'}
                              >
                                <Users className="w-4 h-4 mr-1" />
                                {group.member_count || 0}
                                {expandedGroups[group.slug] ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full text-orange-600 border-orange-200 hover:bg-orange-50"
                                onClick={async () => {
                                  if (!window.confirm(`Remove all test members from "${group.name}"?`)) return;
                                  setActionLoading(`cleanup-${group.slug}`);
                                  try {
                                    const res = await axios.delete(`${API}/admin/cleanup-test-group-members/${group.slug}`);
                                    if (res.data.removed > 0) {
                                      toast.success(`Removed ${res.data.removed} test member(s): ${res.data.users.join(', ')}`);
                                    } else {
                                      toast.info('No test members found in this group');
                                    }
                                    // Reload member list if expanded
                                    if (expandedGroups[group.slug]) {
                                      setGroupMembers(prev => ({ ...prev, [group.slug]: undefined }));
                                      toggleGroupExpanded(group.slug);
                                      setTimeout(() => toggleGroupExpanded(group.slug), 100);
                                    }
                                    refreshCurrentTab();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to remove test members');
                                  } finally {
                                    setActionLoading(null);
                                  }
                                }}
                                disabled={actionLoading === `cleanup-${group.slug}`}
                                data-testid={`cleanup-test-members-${group.slug}`}
                                title="Remove test members"
                              >
                                {actionLoading === `cleanup-${group.slug}` ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <UserX className="w-4 h-4" />
                                )}
                              </Button>
                              <Link to={`/groups/${group.slug}`}>
                                <Button variant="outline" size="sm" className="rounded-full">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="rounded-full"
                                onClick={async () => {
                                  if (!window.confirm(`Delete group "${group.name}"? This cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`${API}/admin/groups/${group.group_id}`);
                                    toast.success('Group deleted');
                                    refreshCurrentTab();
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Failed to delete group');
                                  }
                                }}
                                disabled={actionLoading === group.group_id}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>

                        {/* Expandable member list */}
                        {expandedGroups[group.slug] && (
                          <div className="border-t bg-gray-50/50 px-4 py-3" data-testid={`group-members-panel-${group.slug}`}>
                            {groupMembersLoading[group.slug] ? (
                              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading members...
                              </div>
                            ) : !groupMembers[group.slug] || groupMembers[group.slug].length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-3">No members</p>
                            ) : (
                              <>
                                {/* Sort buttons */}
                                <div className="flex gap-1.5 flex-wrap mb-2">
                                  <span className="text-xs text-muted-foreground self-center mr-1">Sort by:</span>
                                  {[
                                    { key: 'last_name', label: 'Last Name' },
                                    { key: 'city', label: 'City' },
                                    { key: 'country', label: 'Country' },
                                  ].map(s => (
                                    <Button
                                      key={s.key}
                                      size="sm"
                                      variant={(groupMembersSort[group.slug]?.field === s.key) ? 'default' : 'outline'}
                                      className="rounded-full text-xs h-7 px-2.5"
                                      onClick={() => toggleGroupMemberSort(group.slug, s.key)}
                                      data-testid={`group-sort-${group.slug}-${s.key}`}
                                    >
                                      {s.label}
                                      {groupMembersSort[group.slug]?.field === s.key && (
                                        groupMembersSort[group.slug].dir === 'asc'
                                          ? <ArrowUp className="w-3 h-3 ml-0.5" />
                                          : <ArrowDown className="w-3 h-3 ml-0.5" />
                                      )}
                                    </Button>
                                  ))}
                                </div>

                                {/* Member list */}
                                <div className="space-y-1">
                                  {sortGroupMembers(groupMembers[group.slug], groupMembersSort[group.slug]).map((m) => (
                                    <Link 
                                      key={m.user_id} 
                                      to={`/profile/${m.user_id}`}
                                      className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-white transition-colors group"
                                      data-testid={`group-member-${m.user_id}`}
                                    >
                                      <span className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                        {m.name?.charAt(0)?.toUpperCase() || '?'}
                                      </span>
                                      <span className="font-medium text-sm flex-1 truncate group-hover:text-primary transition-colors">{m.name || 'Unknown'}</span>
                                      {m.residence_city && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                          <MapPin className="w-3 h-3" />{m.residence_city}
                                        </span>
                                      )}
                                      {m.residence_country && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                          <Globe className="w-3 h-3" />{m.residence_country}
                                        </span>
                                      )}
                                    </Link>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                        </div>
                      )}
                    </div>

                    {/* Public Groups Section */}
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <Globe className="w-4 h-4 text-primary" />
                        <h3 className="font-bold text-base">Public Groups ({allGroups.filter(g => !g.is_private).length})</h3>
                      </div>
                      <div className="space-y-3">
                        {allGroups.filter(g => !g.is_private).map((group) => (
                      <div key={group.group_id} className="bg-white rounded-xl border-2 border-border overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex gap-4 flex-1 min-w-0">
                              {group.image_url && (
                                <img 
                                  src={group.image_url} 
                                  alt={group.name}
                                  className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-bold text-lg truncate">{group.name}</h3>
                                </div>
                                <p className="text-sm text-muted-foreground line-clamp-2">{group.description}</p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {group.member_count || 0} members
                                </p>
                              </div>
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full"
                                onClick={() => toggleGroupExpanded(group.slug)}
                                data-testid={`toggle-members-${group.slug}`}
                                title={expandedGroups[group.slug] ? 'Hide members' : 'Show members'}
                              >
                                <Users className="w-4 h-4 mr-1" />
                                {group.member_count || 0}
                                {expandedGroups[group.slug] ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />}
                              </Button>
                              <Link to={`/groups/${group.slug}`}>
                                <Button variant="outline" size="sm" className="rounded-full">
                                  <Eye className="w-4 h-4 mr-1" />
                                  View
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                        {/* Expandable members */}
                        {expandedGroups[group.slug] && (
                          <div className="border-t p-4 bg-muted/30">
                            {groupMembersLoading[group.slug] ? (
                              <div className="flex items-center justify-center py-4 text-sm text-muted-foreground">
                                <RefreshCw className="w-4 h-4 animate-spin mr-2" /> Loading members...
                              </div>
                            ) : !groupMembers[group.slug] || groupMembers[group.slug].length === 0 ? (
                              <p className="text-sm text-muted-foreground text-center py-3">No members</p>
                            ) : (
                              <>
                                <div className="flex gap-1.5 flex-wrap mb-2">
                                  <span className="text-xs text-muted-foreground self-center mr-1">Sort by:</span>
                                  {[
                                    { key: 'last_name', label: 'Last Name' },
                                    { key: 'city', label: 'City' },
                                    { key: 'country', label: 'Country' },
                                  ].map(s => (
                                    <Button
                                      key={s.key}
                                      size="sm"
                                      variant={(groupMembersSort[group.slug]?.field === s.key) ? 'default' : 'outline'}
                                      className="rounded-full text-xs h-7 px-2.5"
                                      onClick={() => toggleGroupMemberSort(group.slug, s.key)}
                                    >
                                      {s.label}
                                      {groupMembersSort[group.slug]?.field === s.key && (
                                        groupMembersSort[group.slug].dir === 'asc'
                                          ? <ArrowUp className="w-3 h-3 ml-0.5" />
                                          : <ArrowDown className="w-3 h-3 ml-0.5" />
                                      )}
                                    </Button>
                                  ))}
                                </div>
                                <div className="space-y-1">
                                  {sortGroupMembers(groupMembers[group.slug], groupMembersSort[group.slug]).map((m) => (
                                    <Link 
                                      key={m.user_id} 
                                      to={`/profile/${m.user_id}`}
                                      className="flex items-center gap-2.5 py-1.5 px-2 rounded-lg hover:bg-white transition-colors group"
                                    >
                                      <span className="w-7 h-7 bg-primary/10 rounded-full flex items-center justify-center text-xs font-bold text-primary flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                                        {m.name?.charAt(0)?.toUpperCase() || '?'}
                                      </span>
                                      <span className="font-medium text-sm flex-1 truncate group-hover:text-primary transition-colors">{m.name || 'Unknown'}</span>
                                      {m.residence_city && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                          <MapPin className="w-3 h-3" />{m.residence_city}
                                        </span>
                                      )}
                                      {m.residence_country && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                                          <Globe className="w-3 h-3" />{m.residence_country}
                                        </span>
                                      )}
                                    </Link>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                      </div>
                    </div>
                  </div>
                )}
              </TabsContent>

              {/* Tools Tab */}
              <TabsContent value="tools" className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold">Admin Tools v2</h2>
                </div>

                <div className="grid gap-4">
                  {/* Push Notification Test */}
                  <div className="bg-white rounded-xl border-2 border-indigo-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Bell className="w-5 h-5 text-indigo-500" />
                          Test Push Notification
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send a test push notification to your device. Verifies push is working end-to-end.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                        disabled={actionLoading === 'test-push'}
                        data-testid="test-push-btn"
                        onClick={async () => {
                          setActionLoading('test-push');
                          try {
                            const response = await axios.post(`${API}/push/test`);
                            const d = response.data;
                            if (d.success) {
                              toast.success(d.message);
                            } else {
                              toast.error(`Push test failed: ${d.error}`);
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send test push');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'test-push' ? 'Sending...' : 'Test Push'}
                      </Button>
                    </div>
                  </div>

                  {/* Push Diagnostics — on-device SW log viewer */}
                  <div className="bg-white rounded-xl border-2 border-fuchsia-200 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Bell className="w-5 h-5 text-fuchsia-500" />
                          Push Diagnostics
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Inspect what the service worker actually observed for the last 20 pushes on THIS device — payload, badge_count, and whether <code>navigator.setAppBadge</code> resolved. Best-effort for debugging the Android app-icon red dot.
                        </p>
                      </div>
                      <Link to="/admin/push-diagnostics">
                        <Button
                          variant="outline"
                          className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 whitespace-nowrap"
                          data-testid="push-diagnostics-open-btn"
                        >
                          Open
                        </Button>
                      </Link>
                    </div>
                  </div>

                  {/* Regenerate Event Recaps — force rebuild with latest PII filter */}
                  <div className="bg-white rounded-xl border-2 border-emerald-200 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-emerald-500" />
                          Regenerate Event Recaps
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Force-rebuild every cached "Recap Wrapped" card. Use after tightening the featured-comment picker (PII filter, post-event-only rule) so historic cards drop coordination quotes.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 whitespace-nowrap"
                        disabled={actionLoading === 'regenerate-recaps'}
                        data-testid="regenerate-recaps-btn"
                        onClick={async () => {
                          if (!window.confirm('Rebuild ALL event recap cards? This scans every event with a cached recap and applies the current featured-comment rules.')) return;
                          setActionLoading('regenerate-recaps');
                          try {
                            const response = await axios.post(`${API}/admin/regenerate-event-recaps`);
                            const d = response.data;
                            toast.success(`${d.message} · ${d.rebuilt}/${d.total}${d.errors ? ` (${d.errors} errors)` : ''}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to regenerate recaps');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'regenerate-recaps' ? 'Rebuilding...' : 'Rebuild All'}
                      </Button>
                    </div>
                  </div>

                  {/* Backfill Welcome Emails — one-shot for recent signups */}
                  <div className="bg-white rounded-xl border-2 border-amber-200 p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Mail className="w-5 h-5 text-amber-500" />
                          Backfill Welcome Emails
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Send the Code of Conduct + Safety Guide welcome email to anyone who signed up in the last 7 days but never received it. Idempotent — flips <code>welcome_email_sent</code> so safe to re-run.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-amber-200 text-amber-700 hover:bg-amber-50 whitespace-nowrap"
                        disabled={actionLoading === 'backfill-welcome'}
                        data-testid="backfill-welcome-btn"
                        onClick={async () => {
                          if (!window.confirm('Email every signup from the last 7 days who has not received the welcome email yet?')) return;
                          setActionLoading('backfill-welcome');
                          try {
                            const response = await axios.post(`${API}/admin/backfill-welcome-emails`);
                            const d = response.data;
                            toast.success(`${d.message} · ${d.sent}/${d.candidates}${d.errors ? ` (${d.errors} errors)` : ''}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Backfill failed');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'backfill-welcome' ? 'Sending...' : 'Send Now'}
                      </Button>
                    </div>
                  </div>

                  {/* Clear Pending Connections */}
                  <div className="bg-white rounded-xl border-2 border-border p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-orange-500" />
                          Clear ALL Connection Requests
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Remove all pending AND sent connection requests from all users. This resets everyone's connection requests to start fresh.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-orange-200 text-orange-600 hover:bg-orange-50"
                        disabled={actionLoading === 'clear-connections'}
                        data-testid="clear-pending-connections-btn"
                        onClick={async () => {
                          if (!window.confirm('Are you sure you want to clear ALL connection requests (pending AND sent) from ALL users? This cannot be undone.')) {
                            return;
                          }
                          setActionLoading('clear-connections');
                          try {
                            const response = await axios.post(`${API}/admin/clear-pending-connections`);
                            toast.success(`Cleared! Pending: ${response.data.pending_cleared}, Sent: ${response.data.sent_cleared}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to clear connections');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'clear-connections' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Clearing...
                          </>
                        ) : (
                          'Clear All'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Initialize Connection Fields */}
                  <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
                    <h3 className="font-semibold text-blue-800 mb-2">Initialize Connection Fields</h3>
                    <p className="text-sm text-blue-600 mb-3">
                      Fix connection issues by ensuring all users have the required database fields.
                      Run this if connection requests aren't working properly.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-600 hover:bg-blue-100"
                        disabled={actionLoading === 'init-connections'}
                        data-testid="init-connection-fields-btn"
                        onClick={async () => {
                          setActionLoading('init-connections');
                          try {
                            const response = await axios.post(`${API}/admin/init-connection-fields`);
                            toast.success(`Connection fields initialized! Pending: ${response.data.users_missing_pending}, Sent: ${response.data.users_missing_sent}, Contacts: ${response.data.users_missing_contacts}`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to initialize connection fields');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'init-connections' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Initializing...
                          </>
                        ) : (
                          'Initialize Fields'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Fix Empty Cities */}
                  <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                    <h3 className="font-semibold text-green-800 mb-2">Fix Empty City Fields</h3>
                    <p className="text-sm text-green-600 mb-3">
                      Users with empty residence_city won't appear in search results. 
                      This sets "Budapest, Hungary" for all users missing city data.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-green-200 text-green-600 hover:bg-green-100"
                        disabled={actionLoading === 'fix-cities'}
                        data-testid="fix-empty-cities-btn"
                        onClick={async () => {
                          setActionLoading('fix-cities');
                          try {
                            const response = await axios.post(`${API}/admin/fix-empty-cities?default_city=Budapest&default_country=Hungary`);
                            toast.success(`Fixed ${response.data.users_fixed} users with empty city fields`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to fix city fields');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'fix-cities' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Fixing...
                          </>
                        ) : (
                          'Fix Empty Cities'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Migrate Complimentary Membership */}
                  <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                    <h3 className="font-semibold text-amber-800 mb-2">Migrate Complimentary Membership</h3>
                    <p className="text-sm text-amber-600 mb-3">
                      Sets all existing users to Complimentary Member status with membership expiring 2 years from their signup date.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-amber-200 text-amber-600 hover:bg-amber-100"
                        disabled={actionLoading === 'migrate-membership'}
                        data-testid="migrate-membership-btn"
                        onClick={async () => {
                          if (!window.confirm('This will set ALL users to Complimentary Member with membership_expires = signup date + 2 years. Continue?')) return;
                          setActionLoading('migrate-membership');
                          try {
                            const response = await axios.post(`${API}/payments/migrate-complimentary-membership`);
                            toast.success(`Migration complete! ${response.data.updated} users updated, ${response.data.errors} errors.`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to run migration');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'migrate-membership' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Migrating...
                          </>
                        ) : (
                          'Run Migration'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* 2-Year Free Membership Extension Campaign */}
                  <div className="border-2 rounded-lg p-4 bg-emerald-50 border-emerald-300" data-testid="campaign-2year-section">
                    <h3 className="font-semibold text-emerald-800 mb-2">🎁 2-Year Free Membership Campaign</h3>
                    <p className="text-sm text-emerald-700 mb-3">
                      Extends every real member's <code>membership_expires</code> to <strong>created_at + 2 years</strong> and sends a personalized email announcing the extension.
                      Test / QA accounts (<code>@test.com</code>, <code>@example.com</code>, <code>@t.com</code>) are skipped automatically. Idempotent — safe to re-run.
                    </p>

                    {campaign2yearPreview && (
                      <div className="text-xs bg-white/60 border border-emerald-200 rounded p-3 mb-3 font-mono">
                        <div>Real members: <strong>{campaign2yearPreview.total_real_members}</strong></div>
                        <div>Needs expiry update: <strong>{campaign2yearPreview.needs_update_count}</strong></div>
                        <div>Will email now: <strong>{campaign2yearPreview.will_email_count}</strong></div>
                        <div>Already emailed (skipped): <strong>{campaign2yearPreview.already_emailed_count}</strong></div>
                        <div>Skipped test accounts: <strong>{campaign2yearPreview.skipped_test_accounts}</strong></div>
                        {campaign2yearPreview.sample_will_email?.length > 0 && (
                          <details className="mt-2">
                            <summary className="cursor-pointer text-emerald-700">Sample recipients ({campaign2yearPreview.sample_will_email.length})</summary>
                            <ul className="mt-1 pl-4 list-disc">
                              {campaign2yearPreview.sample_will_email.map((e) => (
                                <li key={e.user_id}>{e.email} → expires {new Date(e.new_expiry).toLocaleDateString()}</li>
                              ))}
                            </ul>
                          </details>
                        )}
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                        disabled={actionLoading === 'campaign-2year-preview'}
                        data-testid="campaign-2year-preview-btn"
                        onClick={async () => {
                          setActionLoading('campaign-2year-preview');
                          try {
                            const response = await axios.get(`${API}/admin/membership-campaign/preview`);
                            setCampaign2yearPreview(response.data);
                            toast.success(`Preview loaded: ${response.data.will_email_count} to email, ${response.data.needs_update_count} to update`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to preview campaign');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'campaign-2year-preview' ? (
                          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Loading Preview…</>
                        ) : (
                          '1. Preview Campaign'
                        )}
                      </Button>

                      <Button
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        disabled={actionLoading === 'campaign-2year-dbonly' || !campaign2yearPreview}
                        data-testid="campaign-2year-dbonly-btn"
                        onClick={async () => {
                          if (!window.confirm('Extend all real members to created_at + 2 years WITHOUT sending emails. Continue?')) return;
                          setActionLoading('campaign-2year-dbonly');
                          try {
                            const response = await axios.post(`${API}/admin/membership-campaign/run?send_emails=false`);
                            toast.success(`Extended ${response.data.updated} members (${response.data.unchanged} already at 2y). No emails sent.`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to run campaign');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'campaign-2year-dbonly' ? (
                          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Extending…</>
                        ) : (
                          '2. Extend Expiry (No Email)'
                        )}
                      </Button>

                      <Button
                        className="bg-emerald-600 text-white hover:bg-emerald-700"
                        disabled={actionLoading === 'campaign-2year-full' || !campaign2yearPreview}
                        data-testid="campaign-2year-run-btn"
                        onClick={async () => {
                          const preview = campaign2yearPreview;
                          if (!preview) return;
                          const confirmMsg = `Run FULL campaign:\n\n` +
                            `• Update ${preview.needs_update_count} members' expiry to created_at + 2 years\n` +
                            `• SEND ${preview.will_email_count} personalized emails\n` +
                            `• Skip ${preview.already_emailed_count} already-emailed members\n\n` +
                            `This will actually send emails. Continue?`;
                          if (!window.confirm(confirmMsg)) return;
                          setActionLoading('campaign-2year-full');
                          try {
                            const response = await axios.post(`${API}/admin/membership-campaign/run`);
                            toast.success(`Done! Updated ${response.data.updated} • Emailed ${response.data.emails_sent} • Errors ${response.data.errors}`);
                            const p = await axios.get(`${API}/admin/membership-campaign/preview`);
                            setCampaign2yearPreview(p.data);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to run campaign');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'campaign-2year-full' ? (
                          <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Running Campaign…</>
                        ) : (
                          '3. Run Full Campaign (Extend + Email)'
                        )}
                      </Button>
                    </div>
                  </div>


                  {/* Fix Country Typos */}
                  <div className="border rounded-lg p-4 bg-purple-50 border-purple-200">
                    <h3 className="font-semibold text-purple-800 mb-2">Fix Country Name Typos</h3>
                    <p className="text-sm text-purple-600 mb-3">
                      Automatically fix common country spelling mistakes like "Humgary" → "Hungary", 
                      "USA" → "United States", etc.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-purple-200 text-purple-600 hover:bg-purple-100"
                        disabled={actionLoading === 'fix-typos'}
                        data-testid="fix-country-typos-btn"
                        onClick={async () => {
                          setActionLoading('fix-typos');
                          try {
                            const response = await axios.post(`${API}/admin/fix-country-typos`);
                            if (response.data.total_fixed > 0) {
                              toast.success(`Fixed ${response.data.total_fixed} country typos!`);
                            } else {
                              toast.info('No typos found to fix');
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to fix country typos');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'fix-typos' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Fixing...
                          </>
                        ) : (
                          'Fix Country Typos'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Grant Free Membership (legacy per-user tool) */}
                  <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
                    <h3 className="font-semibold text-amber-800 mb-2">Grant Free 2-Year Membership (Legacy)</h3>
                    <p className="text-sm text-amber-600 mb-3">
                      Legacy admin tool. For the site-wide 2-year extension use the green
                      <strong> "2-Year Free Membership Campaign" </strong> panel above.
                      Each user's expiry is calculated from <strong>their signup date</strong>,
                      so early members get credit for time served.
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className="border-amber-200 text-amber-600 hover:bg-amber-100"
                        disabled={actionLoading === 'grant-membership'}
                        data-testid="grant-membership-btn"
                        onClick={async () => {
                          if (!window.confirm('Grant free 2-year membership to all users without active membership? Each user gets 2 years from their signup date.')) {
                            return;
                          }
                          setActionLoading('grant-membership');
                          try {
                            const response = await axios.post(`${API}/admin/grant-free-membership`);
                            toast.success(`Granted membership to ${response.data.users_updated} users (based on their signup dates)`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to grant membership');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'grant-membership' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Granting...
                          </>
                        ) : (
                          'Grant Free Membership'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Send Profile Reminders */}
                  <div className="bg-white rounded-xl border-2 border-border p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Mail className="w-5 h-5 text-violet-500" />
                          Send Profile Completion Reminders
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Email all users with incomplete profiles who haven't been reminded yet. Skips test accounts and users with 4+ completed fields.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-violet-200 text-violet-600 hover:bg-violet-50"
                        disabled={actionLoading === 'profile-reminders'}
                        data-testid="send-profile-reminders-btn"
                        onClick={async () => {
                          if (!window.confirm('Send profile completion reminder emails to all eligible users?')) {
                            return;
                          }
                          setActionLoading('profile-reminders');
                          try {
                            const response = await axios.post(`${API}/admin/send-profile-reminders`);
                            toast.success(`Sent ${response.data.sent} reminders, ${response.data.skipped} skipped (complete), ${response.data.failed} failed`);
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send reminders');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'profile-reminders' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          'Send Reminders'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Sync Group Memberships */}
                  <div className="bg-white rounded-xl border-2 border-blue-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <RefreshCw className="w-5 h-5 text-blue-500" />
                          Sync Group Memberships
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Fix notification gaps by reconciling group member lists. Ensures all members receive event emails when new group events are created.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        disabled={actionLoading === 'sync-memberships'}
                        data-testid="sync-group-memberships-btn"
                        onClick={async () => {
                          setActionLoading('sync-memberships');
                          try {
                            const response = await axios.post(`${API}/admin/sync-group-memberships`);
                            const d = response.data;
                            if (d.fixed_users > 0 || d.fixed_groups > 0) {
                              toast.success(`Synced: ${d.fixed_users} user memberships fixed, ${d.fixed_groups} group records updated`);
                            } else {
                              toast.info('All memberships are already in sync');
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to sync memberships');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'sync-memberships' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Syncing...
                          </>
                        ) : (
                          'Sync Now'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Engagement Email Conversion Stats */}
                  {engagementStats && engagementStats.total_emailed > 0 && (
                    <div className="bg-white rounded-xl border-2 border-amber-200 p-6">
                      <h3 className="font-semibold text-lg flex items-center gap-2 mb-4">
                        <TrendingUp className="w-5 h-5 text-amber-500" />
                        Engagement Email Conversions
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-amber-700" data-testid="engagement-total-emailed">{engagementStats.total_emailed}</p>
                          <p className="text-xs text-muted-foreground">Total Emailed</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-green-700" data-testid="engagement-converted">{engagementStats.converted}</p>
                          <p className="text-xs text-muted-foreground">Fully Converted</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-blue-700" data-testid="engagement-partial">{engagementStats.partial}</p>
                          <p className="text-xs text-muted-foreground">Partially Done</p>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold text-gray-700" data-testid="engagement-incomplete">{engagementStats.still_incomplete}</p>
                          <p className="text-xs text-muted-foreground">Still Incomplete</p>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                          <div className="bg-green-500 h-full rounded-full transition-all" style={{ width: `${engagementStats.conversion_rate}%` }} />
                        </div>
                        <span className="text-sm font-bold text-green-700" data-testid="engagement-conversion-rate">{engagementStats.conversion_rate}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">conversion rate (completed both groups + interests)</p>
                    </div>
                  )}

                  {/* Send Engagement Emails */}
                  <div className="bg-white rounded-xl border-2 border-amber-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Mail className="w-5 h-5 text-amber-500" />
                          Send Engagement Emails
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Email members who haven't joined any groups or selected interests. Encourages them to complete their profile and start getting event notifications. Also runs automatically every Wednesday.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-amber-200 text-amber-700 hover:bg-amber-50"
                        disabled={actionLoading === 'engagement-emails'}
                        data-testid="send-engagement-emails-btn"
                        onClick={async () => {
                          if (!window.confirm('Send engagement emails to all members with incomplete profiles?')) return;
                          setActionLoading('engagement-emails');
                          try {
                            const response = await axios.post(`${API}/admin/send-engagement-emails`);
                            const d = response.data;
                            toast.success(`Sent ${d.sent} engagement emails (${d.skipped} skipped)`);
                            // Refresh conversion stats
                            axios.get(`${API}/admin/engagement-stats`).then(r => setEngagementStats(r.data)).catch(() => {});
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to send engagement emails');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'engagement-emails' ? (
                          <>
                            <Mail className="w-4 h-4 mr-2 animate-pulse" />
                            Sending...
                          </>
                        ) : (
                          'Send Now'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Backfill Event Group Names */}
                  <div className="bg-white rounded-xl border-2 border-blue-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Calendar className="w-5 h-5 text-blue-500" />
                          Fix Event Group Names
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          One-time fix: writes the Group name directly onto all existing events so they display the Group instead of Category.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-blue-200 text-blue-600 hover:bg-blue-50"
                        disabled={actionLoading === 'backfill-groups'}
                        data-testid="backfill-event-groups-btn"
                        onClick={async () => {
                          setActionLoading('backfill-groups');
                          try {
                            const response = await axios.post(`${API}/admin/backfill-event-group-names`);
                            const d = response.data;
                            if (d.updated > 0) {
                              toast.success(`Fixed ${d.updated} events with group names`);
                            } else {
                              toast.info('All events already have correct group names');
                            }
                            if (d.groups_not_found?.length > 0) {
                              toast.error(`${d.groups_not_found.length} events have groups that no longer exist`);
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to backfill');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'backfill-groups' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Fixing...
                          </>
                        ) : (
                          'Fix Now'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Clean Up Test Events */}
                  <div className="bg-white rounded-xl border-2 border-red-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Trash2 className="w-5 h-5 text-red-500" />
                          Clean Up Test Events
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Find and delete all events with test-like names (TEST_, Validation, Regression, etc.) and "Test Location" venues.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        disabled={actionLoading === 'cleanup-tests'}
                        data-testid="cleanup-test-events-btn"
                        onClick={async () => {
                          if (!window.confirm('This will permanently delete all test events. Continue?')) {
                            return;
                          }
                          setActionLoading('cleanup-tests');
                          try {
                            const response = await axios.delete(`${API}/admin/cleanup-test-events`);
                            toast.success(`Deleted ${response.data.deleted} test events: ${response.data.events.join(', ')}`);
                            refreshCurrentTab();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to clean up test events');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'cleanup-tests' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Cleaning...
                          </>
                        ) : (
                          'Delete Test Events'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Clean Up Test Group Members */}
                  <div className="bg-white rounded-xl border-2 border-red-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <UserX className="w-5 h-5 text-red-500" />
                          Clean Up Test Group Members
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Remove all test/demo users from every group. Matches names &amp; emails containing "test", "demo", "validation", "regression", "automation", etc.
                          Also fixes stale member counts.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        disabled={actionLoading === 'cleanup-test-members'}
                        data-testid="cleanup-test-group-members-btn"
                        onClick={async () => {
                          if (!window.confirm('Remove all test/demo users from all groups? This cannot be undone.')) return;
                          setActionLoading('cleanup-test-members');
                          try {
                            const response = await axios.delete(`${API}/admin/cleanup-test-group-members`);
                            if (response.data.removed > 0) {
                              toast.success(`Removed ${response.data.removed} test members from ${response.data.groups_affected} groups`);
                            } else {
                              toast.info('No test members found in any group');
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to clean up test group members');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'cleanup-test-members' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Cleaning...
                          </>
                        ) : (
                          'Remove Test Members'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Clean Up Test Listings */}
                  <div className="bg-white rounded-xl border-2 border-red-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <ShoppingBag className="w-5 h-5 text-red-500" />
                          Clean Up Test Listings
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Delete all test listings across Jobs, Housing, Marketplace, and Businesses. 
                          Matches titles/names containing "test", "validation", "regression", "dummy", "sample listing", etc.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        disabled={actionLoading === 'cleanup-test-listings'}
                        data-testid="cleanup-test-listings-btn"
                        onClick={async () => {
                          if (!window.confirm('Delete all test listings across Jobs, Housing, Marketplace, and Businesses? This cannot be undone.')) return;
                          setActionLoading('cleanup-test-listings');
                          try {
                            const response = await axios.delete(`${API}/admin/cleanup-test-listings`);
                            const d = response.data.details;
                            const parts = [];
                            if (d.jobs.deleted > 0) parts.push(`${d.jobs.deleted} jobs`);
                            if (d.housing.deleted > 0) parts.push(`${d.housing.deleted} housing`);
                            if (d.marketplace.deleted > 0) parts.push(`${d.marketplace.deleted} marketplace`);
                            if (d.businesses.deleted > 0) parts.push(`${d.businesses.deleted} businesses`);
                            if (parts.length > 0) {
                              toast.success(`Deleted ${response.data.total_deleted} test listings: ${parts.join(', ')}`);
                            } else {
                              toast.info('No test listings found');
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to clean up test listings');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'cleanup-test-listings' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Cleaning...
                          </>
                        ) : (
                          'Delete Test Listings'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Clean Up Test Groups */}
                  <div className="bg-white rounded-xl border-2 border-red-200 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Users className="w-5 h-5 text-red-500" />
                          Clean Up Test Groups
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Delete all groups with test-like names or slugs starting with "test-". Also removes group references from user profiles.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        className="border-red-200 text-red-600 hover:bg-red-50"
                        disabled={actionLoading === 'cleanup-test-groups'}
                        data-testid="cleanup-test-groups-btn"
                        onClick={async () => {
                          if (!window.confirm('Delete all test groups? This cannot be undone.')) return;
                          setActionLoading('cleanup-test-groups');
                          try {
                            const response = await axios.delete(`${API}/admin/cleanup-test-groups`);
                            if (response.data.deleted > 0) {
                              toast.success(`Deleted ${response.data.deleted} test groups`);
                            } else {
                              toast.info('No test groups found');
                            }
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to clean up test groups');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'cleanup-test-groups' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Cleaning...
                          </>
                        ) : (
                          'Delete Test Groups'
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Delete ALL Listings */}
                  <div className="bg-white rounded-xl border-2 border-red-300 p-6">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                          Delete All Listings
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          Permanently delete <strong>ALL</strong> listings across Jobs, Housing, Marketplace, and Businesses. 
                          Use this when all existing listings are test data and need a fresh start.
                        </p>
                      </div>
                      <Button
                        variant="destructive"
                        disabled={actionLoading === 'delete-all-listings'}
                        data-testid="delete-all-listings-btn"
                        onClick={async () => {
                          if (!window.confirm('WARNING: This will delete ALL listings across Jobs, Housing, Marketplace, and Businesses. Are you absolutely sure?')) return;
                          if (!window.confirm('This is your FINAL confirmation. All listings will be permanently deleted. Continue?')) return;
                          setActionLoading('delete-all-listings');
                          try {
                            const response = await axios.delete(`${API}/admin/delete-all-listings`);
                            const d = response.data.details;
                            toast.success(`Deleted all listings: ${d.jobs} jobs, ${d.housing} housing, ${d.marketplace} marketplace, ${d.businesses} businesses`);
                            refreshCurrentTab();
                          } catch (error) {
                            toast.error(error.response?.data?.detail || 'Failed to delete listings');
                          } finally {
                            setActionLoading(null);
                          }
                        }}
                      >
                        {actionLoading === 'delete-all-listings' ? (
                          <>
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                            Deleting...
                          </>
                        ) : (
                          'Delete All Listings'
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
              
              {/* Polls Moderation Tab */}
              <TabsContent value="polls" className="space-y-4">
                <PollsModerationTab />
              </TabsContent>
              
              {/* Deleted Events (Trash) Tab */}
              <TabsContent value="trash" className="space-y-4">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-xl font-bold flex items-center gap-2">
                      <Trash2 className="w-5 h-5 text-red-500" />
                      Recently Deleted Events
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Events soft-deleted in the last 30 days. Restore or permanently erase below.
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-red-50 text-red-700 text-sm font-semibold rounded-full">
                    {deletedEventsList.length} total
                  </span>
                </div>
                
                {deletedEventsList.length === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-border">
                    <Trash2 className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                    <p className="text-muted-foreground">No deleted events</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {deletedEventsList.map((event) => (
                      <div
                        key={event.event_id}
                        className="bg-white rounded-2xl border-2 border-red-100 p-5"
                        data-testid={`admin-deleted-event-${event.event_id}`}
                      >
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-bold truncate">{event.title}</h3>
                              <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-semibold rounded-full">
                                Deleted
                              </span>
                              {event.days_left !== undefined && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-semibold rounded-full">
                                  {event.days_left}d left
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground space-y-0.5">
                              <p>
                                <Calendar className="w-3.5 h-3.5 inline mr-1" />
                                {new Date(event.event_date).toLocaleDateString()} · {event.city || '—'}, {event.country || '—'}
                              </p>
                              <p>
                                Creator: <strong>{event.creator?.name || event.creator_id}</strong>
                                {event.creator?.email && <span className="text-xs"> ({event.creator.email})</span>}
                              </p>
                              <p className="text-xs">
                                Deleted by <strong>{event.deleted_by_user?.name || event.deleted_by}</strong> on{' '}
                                {event.deleted_at ? new Date(event.deleted_at).toLocaleString() : '—'}
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex gap-2 shrink-0">
                            <Button
                              onClick={() => handleRestoreDeletedEvent(event.event_id, event.title)}
                              disabled={restoringEventId === event.event_id}
                              className="bg-purple-600 hover:bg-purple-700"
                              size="sm"
                              data-testid={`admin-restore-${event.event_id}`}
                            >
                              <RotateCcw className="w-4 h-4 mr-1.5" />
                              {restoringEventId === event.event_id ? 'Restoring...' : 'Restore'}
                            </Button>
                            <Button
                              onClick={() => handlePermanentDelete(event.event_id, event.title)}
                              disabled={permanentDeletingId === event.event_id}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              data-testid={`admin-permanent-delete-${event.event_id}`}
                            >
                              <Trash2 className="w-4 h-4 mr-1.5" />
                              {permanentDeletingId === event.event_id ? 'Erasing...' : 'Erase Forever'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>

            {/* Article Create/Edit Dialog */}
            <Dialog open={showArticleDialog} onOpenChange={setShowArticleDialog}>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingArticle ? 'Edit Article' : 'Create New Article'}
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="title">Title *</Label>
                      <Input
                        id="title"
                        value={articleForm.title}
                        onChange={(e) => {
                          setArticleForm({
                            ...articleForm,
                            title: e.target.value,
                            slug: editingArticle ? articleForm.slug : generateSlug(e.target.value)
                          });
                        }}
                        placeholder="e.g., Top 10 Tips for Moving Abroad"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="slug">URL Slug</Label>
                      <Input
                        id="slug"
                        value={articleForm.slug}
                        onChange={(e) => setArticleForm({...articleForm, slug: e.target.value})}
                        placeholder="auto-generated"
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={articleForm.category}
                        onValueChange={(value) => setArticleForm({...articleForm, category: value})}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ARTICLE_CATEGORIES.map((cat) => (
                            <SelectItem key={cat.id} value={cat.id}>
                              {cat.icon} {cat.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="excerpt">Excerpt (Short description)</Label>
                    <Textarea
                      id="excerpt"
                      value={articleForm.excerpt}
                      onChange={(e) => setArticleForm({...articleForm, excerpt: e.target.value})}
                      placeholder="A brief summary that appears on the guides listing..."
                      rows={2}
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="content">Content * (Markdown supported)</Label>
                    <Textarea
                      id="content"
                      value={articleForm.content}
                      onChange={(e) => setArticleForm({...articleForm, content: e.target.value})}
                      placeholder="Write your article content here. You can use Markdown formatting..."
                      rows={12}
                      className="mt-1 font-mono text-sm"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="tags">Tags (comma-separated)</Label>
                    <Input
                      id="tags"
                      value={articleForm.tags}
                      onChange={(e) => setArticleForm({...articleForm, tags: e.target.value})}
                      placeholder="e.g., relocation, tips, visa"
                      className="mt-1"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="featured_image">Featured Image URL (optional)</Label>
                    <Input
                      id="featured_image"
                      value={articleForm.featured_image}
                      onChange={(e) => setArticleForm({...articleForm, featured_image: e.target.value})}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowArticleDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveArticle}
                      disabled={savingArticle}
                      className="bg-primary"
                    >
                      {savingArticle ? 'Saving...' : (editingArticle ? 'Update Article' : 'Create Article')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Event Image Edit Dialog */}
            <Dialog open={showEventImageDialog} onOpenChange={setShowEventImageDialog}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>Edit Event Image</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {editingEvent && (
                    <>
                      <p className="text-sm text-muted-foreground">
                        Editing image for: <strong>{editingEvent.title}</strong>
                      </p>
                      
                      {/* Current Image Preview */}
                      <div className="space-y-2">
                        <Label>Current Image</Label>
                        <div className="border rounded-lg overflow-hidden h-40 bg-muted">
                          {(eventImageFile || editingEvent.image_url) ? (
                            <img 
                              src={eventImageFile || editingEvent.image_url} 
                              alt="Preview"
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="flex items-center justify-center h-full text-muted-foreground">
                              No image
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Upload New Image */}
                      <div className="space-y-2">
                        <Label>Upload New Image</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleEventImageFileChange}
                            className="flex-1"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Max 2MB. Supports JPG, PNG, WebP.
                        </p>
                      </div>

                      {/* Or Use URL */}
                      <div className="space-y-2">
                        <Label>Or Enter Image URL</Label>
                        <Input
                          type="url"
                          placeholder="https://images.unsplash.com/..."
                          value={eventImageUrl}
                          onChange={(e) => {
                            setEventImageUrl(e.target.value);
                            setEventImageFile(null); // Clear file when URL is entered
                          }}
                        />
                      </div>

                      {/* New Image Preview */}
                      {(eventImageFile || eventImageUrl) && (
                        <div className="space-y-2">
                          <Label>New Image Preview</Label>
                          <div className="border rounded-lg overflow-hidden h-40 bg-muted">
                            <img 
                              src={eventImageFile || eventImageUrl} 
                              alt="New preview"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowEventImageDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSaveEventImage}
                    disabled={savingEventImage || (!eventImageUrl && !eventImageFile)}
                  >
                    {savingEventImage ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Save Image
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;

// ---------------------------------------------------------------------------
// Polls Moderation tab (admin only).
// City-scoped polls await admin approval before publishing to prevent spam.
// ---------------------------------------------------------------------------
function PollsModerationTab() {
  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busyId, setBusyId] = useState(null);

  const fetchPending = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/polls/admin/pending`);
      setPolls(r.data?.polls || []);
    } catch {
      setPolls([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPending();
  }, []);

  const handleApprove = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/polls/admin/${id}/approve`);
      toast.success('Poll approved');
      setPolls((prev) => prev.filter((p) => p.poll_id !== id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not approve');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/polls/admin/${id}/reject`, { reason: rejectReason });
      toast.success('Poll rejected');
      setPolls((prev) => prev.filter((p) => p.poll_id !== id));
      setRejectingId(null);
      setRejectReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not reject');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-polls-tab">
      <div className="mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-violet-600" />
          City Polls Awaiting Review
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          City-scoped polls require admin approval to keep the feed spam-free.
          After 3 approved polls, the creator's future city polls auto-publish.
        </p>
      </div>

      {loading && <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>}

      {!loading && polls.length === 0 && (
        <div className="text-center py-12 text-muted-foreground border-2 border-dashed rounded-2xl">
          <Check className="w-10 h-10 mx-auto mb-2 text-green-500" />
          <p className="font-semibold">All caught up!</p>
          <p className="text-sm">No city polls waiting for review right now.</p>
        </div>
      )}

      <div className="space-y-3">
        {polls.map((p) => (
          <div
            key={p.poll_id}
            className="border-2 rounded-2xl p-4 bg-white"
            data-testid={`admin-poll-pending-${p.poll_id}`}
          >
            <div className="flex items-start gap-3">
              {p.creator?.profile_picture ? (
                <img src={p.creator.profile_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-semibold">
                  {(p.creator?.name || '?')[0]}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{p.creator?.name || 'Unknown'}</p>
                <p className="text-xs text-muted-foreground">
                  {p.creator?.email} · {p.city || 'Unknown city'}{p.country ? `, ${p.country}` : ''}
                </p>
                <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                  <span>{p.creator_approved_count || 0} approved</span>
                  <span>{p.creator_rejected_count || 0} rejected</span>
                </div>
              </div>
            </div>

            <p className="mt-3 font-bold text-base">{p.question}</p>
            {p.description && (
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{p.description}</p>
            )}
            {p.image_url && (
              <img src={p.image_url} alt="" className="mt-2 max-h-40 rounded-lg" />
            )}
            <ul className="mt-2 text-sm text-gray-700 list-disc list-inside">
              {(p.options || []).map((opt) => (
                <li key={opt.id}>{opt.text}</li>
              ))}
            </ul>

            {rejectingId === p.poll_id ? (
              <div className="mt-3 space-y-2">
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason (shared with the creator)"
                  rows={2}
                  data-testid={`admin-poll-reject-reason-${p.poll_id}`}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setRejectingId(null); setRejectReason(''); }}
                    disabled={busyId === p.poll_id}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleReject(p.poll_id)}
                    disabled={busyId === p.poll_id}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid={`admin-poll-reject-confirm-${p.poll_id}`}
                  >
                    {busyId === p.poll_id ? 'Rejecting…' : 'Confirm Reject'}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={() => handleApprove(p.poll_id)}
                  disabled={busyId === p.poll_id}
                  className="bg-green-600 hover:bg-green-700 flex-1"
                  data-testid={`admin-poll-approve-${p.poll_id}`}
                >
                  {busyId === p.poll_id ? 'Working…' : 'Approve'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setRejectingId(p.poll_id)}
                  disabled={busyId === p.poll_id}
                  className="flex-1"
                  data-testid={`admin-poll-reject-${p.poll_id}`}
                >
                  Reject
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

